import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL, EXPLORER_API_BASE_URL } from '../config/api';
import { spaceIdFromInput, isSpaceId } from '../utils/spaceIdFromInput';
import { isRealHuman, isVisitingAgent, agentModelName } from '../utils/playerKind';
import AdminBugReports from '../components/AdminBugReports';
import AdminEventFeedback from '../components/AdminEventFeedback'; // 🎬📝 09-23 the credits card's answers

const ADMIN_KEY_LS = 'adminApiKey';

// ── UI kit (07-22 readability pass) ─────────────────────────────────────────
// The page used to paint dark cards on the app's white body with inherited
// (near-black) heading/table text — unreadable. Everything below pins an
// explicit dark shell + explicit text colors so nothing depends on app CSS.
const FONT_UI = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif";
const FONT_MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const S = {
  input: { padding: '8px 10px', background: '#0c1016', color: '#e8edf4', border: '1px solid #39455a', borderRadius: 6, fontSize: 13.5, fontFamily: FONT_UI },
  th: { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#8b98a9', whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', fontSize: 13, color: '#dde3ea' },
  blurb: { margin: '0 0 14px', color: '#9aa7b8', fontSize: 13, lineHeight: 1.55, maxWidth: 860 },
  groupLabel: { fontSize: 11, color: '#8b98a9', textTransform: 'uppercase', letterSpacing: 0.5 },
};

const BTN_KINDS = {
  primary: { background: '#2e7d32', color: '#fff', fontWeight: 700 },
  blue: { background: '#2b6cb0', color: '#fff' },
  warn: { background: '#8a6d1f', color: '#fff' },
  danger: { background: '#b23b3b', color: '#fff' },
  subtle: { background: '#242e3d', color: '#dde3ea' },
};

const Btn = ({ kind = 'subtle', style, disabled, ...rest }) => (
  <button
    {...rest}
    disabled={disabled}
    style={{
      padding: '8px 14px', border: 'none', borderRadius: 6, fontSize: 13, fontFamily: FONT_UI,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, whiteSpace: 'nowrap',
      ...BTN_KINDS[kind], ...style,
    }}
  />
);

// Labeled input wrapper — replaces the old placeholder-only fields, which
// made the launch form a guessing game once anything was typed in them.
const Field = ({ label, children, style }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, ...style }}>
    <span style={S.groupLabel}>{label}</span>
    {children}
  </label>
);

const PILL_TONES = {
  live: { background: '#3a1518', color: '#ff7b7b', border: '1px solid #7c2a2e' },
  ok: { background: '#14301b', color: '#7fe08f', border: '1px solid #2e6b3a' },
  idle: { background: '#242e3d', color: '#9aa7b8', border: '1px solid #39455a' },
  warn: { background: '#332a12', color: '#e0c36a', border: '1px solid #6b5a1f' },
};

const Pill = ({ tone = 'idle', children }) => (
  <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, verticalAlign: 'middle', ...PILL_TONES[tone] }}>
    {children}
  </span>
);

const Card = ({ id, accent = '#39455a', title, blurb, children }) => (
  <section
    id={id}
    style={{
      marginBottom: 20, padding: '18px 20px', background: '#151b26', borderRadius: 10,
      border: '1px solid #26303f', borderLeft: `3px solid ${accent}`, scrollMarginTop: 70,
    }}
  >
    <h2 style={{ margin: '0 0 4px', fontSize: 17, color: '#fff' }}>{title}</h2>
    {blurb && <p style={S.blurb}>{blurb}</p>}
    {children}
  </section>
);

// Plain-language first line, raw JSON tucked behind a <details> — the old
// bare JSON dumps made every outcome look like an error.
const ResultPane = ({ data, onDismiss }) => {
  if (!data) return null;
  const ok = !!data.success;
  return (
    <div style={{
      margin: '12px 0 0', padding: '10px 14px', borderRadius: 8, fontSize: 13,
      background: ok ? '#14301b' : '#361619', color: ok ? '#a9e8b2' : '#f5a3a3',
      border: `1px solid ${ok ? '#2e6b3a' : '#7c2a2e'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <strong style={{ flex: 1 }}>
          {ok ? '✓ Done' : `✗ Failed${data.error ? ` — ${String(data.error)}` : ''}`}
        </strong>
        {onDismiss && (
          <button onClick={onDismiss} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 13 }}>✕</button>
        )}
      </div>
      <details style={{ marginTop: 4 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, opacity: 0.8 }}>details</summary>
        <pre style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap', fontFamily: FONT_MONO, fontSize: 12 }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  );
};

const NAV = [
  ['explorer', '🦀 Explorer'],
  ['show', '🎛 Show'],
  ['watch', '📺 Watch-Along'],
  ['reports', '🛡️ Reports'],
  ['eggs', '🥚 Eggs'],
  ['kick', '👢 Kick'],
  ['games', '🎮 Spaces'],
  ['agents', '🤖 Agents'],
  ['music', '🎵 Song'],
];

// Spaces table shows only the newest N games by default — the full history
// (hundreds of rows) buried the live ones. "Show all" reveals the rest.
const GAMES_SHOWN = 25;

// Probe-suite spaces (Nina 08-04: hide from the admin list by default).
// Every current probe tool names its space with "Probe" (whodunnit stage/dock,
// comp-places); name is the only probe signal the list response carries — the
// machine flag (data.human_player_identifier = player_probe_*) isn't in the
// adminListChatGames payload.
const isProbeGame = (g) => /probe/i.test(g.name || '');

// "24m" / "1.2h" — avg join→last-action span of the humans who acted.
const fmtAvgMinutes = (mins) => {
  if (mins == null) return null;
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  return `${(mins / 60).toFixed(1)}h`;
};

// "3m" / "5h" / "2d" — how long ago, for the Last used column.
const fmtAgo = (iso) => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
};

// 👤🤖 Seat kind badge. Reads the REAL discriminators through playerKind.js,
// never `is_human` — a connected agent is seated is_human:true on purpose, and
// reading that flag as "a person" is what made the Claude House report 69
// humans when 65 were real (the other 4 were our own CapFill/SmokeProbe seats).
const SeatKind = ({ p }) => {
  const person = isRealHuman(p);
  const guest = isVisitingAgent(p);
  const tone = person ? '#7fd18c' : guest ? '#e0b978' : '#8fa4c8';
  const label = person ? '🧑 person' : guest ? '🤖 guest agent' : '🤖 agent';
  const model = agentModelName(p);
  return (
    <span
      title={person
        ? 'A real person — is_human and not an external agent.'
        : `External agent seat${model ? ` · ${model}` : ''}${p.agent_type ? ` · ${p.agent_type}` : ''}. Seated is_human=${String(!!p.is_human)} by design.`}
      style={{
        display: 'inline-block', marginRight: 8, padding: '1px 7px', borderRadius: 999,
        fontSize: 10.5, fontWeight: 600, color: tone, border: `1px solid ${tone}44`, background: `${tone}14`,
      }}
    >
      {label}
    </span>
  );
};

// Tooltip for a seat's IP cell — says WHY a cell is empty rather than leaving
// a bare dash that reads as "nobody knows".
const ipTitle = (p) => {
  if (!p.join_ip) {
    return p.is_external_agent
      ? 'No invite IP: agent seats only began storing it on 08-16.'
      : 'No join IP: human seats only began storing it on 08-11.';
  }
  const trail = Array.isArray(p.join_ips) && p.join_ips.length
    ? p.join_ips.map(e => `${e.ip} — ${new Date(e.at).toLocaleString()}`).join('\n')
    : null;
  return trail ? `Seen from:\n${trail}` : `Captured at join: ${p.join_ip}`;
};

// Sort key for "Recently used". A person beats a bot: a space a human touched
// ranks above one where only resident AI has been talking, and creation date is
// the last resort. Without this the list was pure created_at and the live house
// (minted 08-08, busy every day since) sat ~500 rows down under probe spaces.
const usedRank = (g) => Math.max(
  g.last_human_at ? new Date(g.last_human_at).getTime() : 0,
  g.last_activity_at ? new Date(g.last_activity_at).getTime() : 0,
  g.created_at ? new Date(g.created_at).getTime() : 0,
);

const AdminPage = () => {
  const [chatGames, setChatGames] = useState([]);
  const [showAllGames, setShowAllGames] = useState(false);
  const [showProbeGames, setShowProbeGames] = useState(false);
  const [hideCompGames, setHideCompGames] = useState(false);
  // 'used' (default) = newest human touch first; 'created' = the old date order.
  const [gamesSort, setGamesSort] = useState('used');
  // Free-text filter over name + id. 1000+ rows with no search meant a space you
  // knew existed (the Claude House) was unreachable in practice.
  const [gameSearch, setGameSearch] = useState('');
  // Private = data.is_shared !== true (never on /explore). Off by default;
  // toggling filters the table down to just the private ones.
  const [showPrivateOnly, setShowPrivateOnly] = useState(false);
  // OpenRouter spend is account-wide only — no per-space cost is recorded
  // anywhere (OPENROUTER_CREDIT_AUDIT_2026_08_04), so this is one strip for
  // the whole key, not a table column.
  const [orSpend, setOrSpend] = useState(null);
  // 💸📊 Per-space AI spend attribution (token economy Phase 0, ai_spend_log).
  // Soft-absent until the deployed backend has logged its first call.
  const [spendAttr, setSpendAttr] = useState(null);
  const [bugCounts, setBugCounts] = useState(null); // 🐛 09-20: the v1 bug-report notification = this badge
  const [showSpendAttr, setShowSpendAttr] = useState(false);
  // Remove-space popup: the game object being removed, or null.
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  // 🤖 Marketplace agents (08-14, Nina: same lever as spaces). Hide pulls
  // the marketplace listing (is_public=false); Retire also flips is_active
  // (stops roaming self-joins). No hard delete on purpose.
  const [adminAgents, setAdminAgents] = useState([]);
  const [agentActionId, setAgentActionId] = useState(null);
  // 🎵 Set-the-song tool (Nina 08-14): the only way to change a space's
  // soundtrack after creation — music_url is write-once everywhere else.
  const [musicGameId, setMusicGameId] = useState('');
  const [musicUrl, setMusicUrl] = useState('');
  const [musicBusy, setMusicBusy] = useState(false);
  // Coarse geo per creator IP, looked up in one backend batch call after the
  // list loads. null value = looked up, no result (keeps failed IPs from
  // refetching forever).
  const [geoByIp, setGeoByIp] = useState({});
  // IPs the lookup service didn't really answer (busy, out of time): retried
  // on a list refresh after this time, never in a tight loop.
  const geoRetryAt = useRef({});
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [result, setResult] = useState(null);
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_LS) || '');
  const [keyInput, setKeyInput] = useState('');
  const [expandedGameId, setExpandedGameId] = useState(null);
  const [humansByGame, setHumansByGame] = useState({});
  const [kicking, setKicking] = useState({});
  const [kickName, setKickName] = useState('');
  const [kickScope, setKickScope] = useState('all_games');
  // 🦀 Bot the Explorer — manual launch with an optional destination URL.
  // Backend routes already exist (adminExplorerStart/Stop/Status + the
  // one-shot vote override); this is just the button Nina asked for (07-13).
  const [explorerStatus, setExplorerStatus] = useState(null);
  const [explorerUrl, setExplorerUrl] = useState('');
  const [explorerLabel, setExplorerLabel] = useState('');
  // Field briefing (07-17): optional free-text intel that rides the armed
  // destination INSIDE the bot's note-to-self (site quirks, must-sees,
  // known-broken corners). Works with the picker AND custom URLs.
  const [explorerBriefing, setExplorerBriefing] = useState('');
  const [explorerBusy, setExplorerBusy] = useState(false);
  const [explorerResult, setExplorerResult] = useState(null);
  // Session model picker ('' = keep the current sticky choice). A picked
  // model rides this launch AND persists for future scheduled sessions;
  // 'default' clears the override back to the code default (Haiku).
  const [explorerModel, setExplorerModel] = useState('');
  // Per-launch session length (Nina 07-19). '' = default cap. NOT sticky.
  const [explorerDuration, setExplorerDuration] = useState('');
  // Expedition destination (user decision 07-15: expeditions are admin
  // opt-in ONLY — the ballot winner no longer auto-rides). '' = no
  // expedition; a candidate id arms the one-shot override at launch.
  const [explorerDest, setExplorerDest] = useState('');
  const [explorerBallot, setExplorerBallot] = useState(null);
  // Full candidate pool (Nina 07-25: "ALL the options on the dropdown") —
  // the ballot is only the day's 4-slot shuffle; the picker should offer
  // every pool entry. The override endpoint already resolves candidate_id
  // against the full pool, so this is display-only.
  const [explorerPool, setExplorerPool] = useState(null);
  // Jukebox starting song (07-16): persistent opener — plays first when a
  // session's music queue is empty, every session, until cleared.
  const [jukeboxSeed, setJukeboxSeed] = useState(null);
  const [jukeboxUrl, setJukeboxUrl] = useState('');
  const [jukeboxBusy, setJukeboxBusy] = useState(false);
  const [jukeboxMsg, setJukeboxMsg] = useState('');
  // Pre-show knob (07-20): 'house' = house song opens at launch (default);
  // 'theme' = classic suit-up theme through boot, song waits for the agent.
  const [jukeboxPreshow, setJukeboxPreshow] = useState('house');
  // Chibi voice knob (07-20): 'tts' spoken / 'babble' animalese / 'both' =
  // babble the captions, spoken takes on the big lines only. Default is
  // CONTEXTUAL since 07-29: key-absent = 'tts' on expeditions, 'both' auto
  // during armed watch-alongs (matchday/movie) — picking Spoken returns to
  // that; Babble/Both pin a sticky override.
  const [explorerVoice, setExplorerVoice] = useState('tts');
  const [explorerVoiceMsg, setExplorerVoiceMsg] = useState('');

  // ── Marketplace expedition (chibi port spec §4.1) ────────────────────────
  // Launches a PORTED agent in the chibi body via jsg's registerAgent trigger
  // (POST /api/agents/register {action:'launch_explorer'}) — a DIFFERENT
  // service than the classic launcher above: explorer-backend's
  // /admin/explorer/start has no agent_id and always launches the house
  // Claude (port spec 07-30 ledger). Shares the same single body slot.
  // The id field is the source of truth — private agents (is_public:false)
  // never appear in the public dropdown, so paste their id. Last-used id
  // sticks in localStorage.
  const [agentLaunchId, setAgentLaunchId] = useState(() => localStorage.getItem('adminAgentLaunchId') || '');
  const [agentLaunchDuration, setAgentLaunchDuration] = useState('15');
  const [agentLaunchBusy, setAgentLaunchBusy] = useState(false);
  const [agentLaunchResult, setAgentLaunchResult] = useState(null);
  const [marketplaceAgents, setMarketplaceAgents] = useState([]);
  // Destination for the direct launcher (queue handoff §2: the classic
  // launcher's dropdown + custom-URL pair, custom URL outranks — copied, not
  // reinvented). '' / empty = Spaces wander, byte-identical to before.
  const [agentLaunchDest, setAgentLaunchDest] = useState('');
  const [agentLaunchUrl, setAgentLaunchUrl] = useState('');
  // ── Expedition queue (queue handoff §5.6): owners request, Nina approves
  // each one (ruling ⑩). The override pair below lets her REPLACE the
  // owner's destination pick at approval (custom URL outranks id).
  const [expeditionQueue, setExpeditionQueue] = useState([]);
  const [expeditionQueueLive, setExpeditionQueueLive] = useState(false);
  const [queueBusyId, setQueueBusyId] = useState(null);
  const [queueResult, setQueueResult] = useState(null);
  const [queueOverrideDest, setQueueOverrideDest] = useState('');
  const [queueOverrideUrl, setQueueOverrideUrl] = useState('');
  // Destination options for the TWO marketplace dropdowns come from the jsg
  // public read, NOT explorerPickerOptions: the classic picker's admin feed
  // falls back to explorer-backend's in-code seed when Redis is empty, but
  // jsg resolves ids against Redis ONLY — seed-fed ids would 400 at launch
  // (review confirm 08-12). Same source as the resolver = no dead options.
  const [marketDestPool, setMarketDestPool] = useState([]);
  const fetchExpeditionQueue = useCallback(async () => {
    if (!adminKey) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify({ action: 'list_expedition_requests' }),
      });
      const data = await res.json();
      if (data.success) {
        setExpeditionQueue(Array.isArray(data.queue) ? data.queue : []);
        setExpeditionQueueLive(!!data.live);
      }
    } catch (err) { /* list refreshes on the next action */ }
    try {
      const poolRes = await fetch(`${API_BASE_URL}/api/expeditionQueue/public`);
      const poolData = await poolRes.json();
      if (poolData.success && Array.isArray(poolData.destinations)) setMarketDestPool(poolData.destinations);
    } catch (err) { /* dropdowns fall back to wander + custom URL */ }
  }, [adminKey]);
  useEffect(() => { fetchExpeditionQueue(); }, [fetchExpeditionQueue]);
  useEffect(() => {
    if (!adminKey) return;
    fetch(`${API_BASE_URL}/api/agents/marketplace?limit=100&sort=newest`)
      .then((r) => r.json())
      .then((d) => setMarketplaceAgents(Array.isArray(d?.agents) ? d.agents : []))
      .catch(() => { /* dropdown is a convenience; the id field always works */ });
  }, [adminKey]);

  // Admin key rides as a query param, not an X-Admin-Key header: the custom
  // header triggers a CORS preflight that API Gateway's OPTIONS mock rejects
  // (fixing that needs a full CFN deploy). handler.js checkAdminAuth accepts
  // queryStringParameters.admin_key as an equivalent.
  const adminFetch = useCallback(async (url, opts = {}) => {
    const sep = url.includes('?') ? '&' : '?';
    return fetch(`${url}${sep}admin_key=${encodeURIComponent(adminKey)}`, opts);
  }, [adminKey]);

  const fetchChatGames = useCallback(async () => {
    if (!adminKey) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminListChatGames=true`);
      const data = await res.json();
      if (data._adminUnauthorized) {
        setResult({ success: false, error: data.error });
        localStorage.removeItem(ADMIN_KEY_LS);
        setAdminKey('');
      } else {
        setChatGames(data.games || []);
      }
    } catch (err) {
      console.error('Failed to fetch chat games:', err);
    }
    setLoading(false);
  }, [adminKey, adminFetch]);

  useEffect(() => { fetchChatGames(); }, [fetchChatGames]);

  const fetchAdminAgents = useCallback(async () => {
    if (!adminKey) return;
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminListAgents=true`);
      const data = await res.json();
      if (data.success) setAdminAgents(data.agents || []);
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    }
  }, [adminKey, adminFetch]);

  useEffect(() => { fetchAdminAgents(); }, [fetchAdminAgents]);

  const setSpaceMusic = async () => {
    // 🔗 Paste a share URL OR a bare id (Nina 08-15). Every share surface hands
    // out `/game-step?gameId=<uuid>`, so that link was already on the clipboard
    // while this field demanded a uuid dug out of the Spaces table. Unparseable
    // input passes through untouched, so a bare id still behaves exactly as before.
    const id = spaceIdFromInput(musicGameId);
    const url = musicUrl.trim();
    if (!id || !url) { setResult({ success: false, error: 'Need a space (ID or share link) and a YouTube URL.' }); return; }
    setMusicBusy(true);
    try {
      const res = await adminFetch(
        `${API_BASE_URL}/api/takeGameStep?adminSetSpaceMusic=true&gameId=${encodeURIComponent(id)}&url=${encodeURIComponent(url)}`,
        { method: 'POST' }
      );
      const data = await res.json();
      setResult(data);
      if (data.success) setMusicUrl('');
    } catch (err) {
      setResult({ success: false, error: err.message });
    }
    setMusicBusy(false);
  };

  const agentAction = async (agent, mode) => {
    const verb = mode === 'hide' ? 'Hide from the marketplace' : mode === 'retire' ? 'Retire (hide + deactivate roaming)' : 'Restore';
    if (mode !== 'restore' && !window.confirm(`${verb}: ${agent.name}?`)) return;
    setAgentActionId(agent.id);
    try {
      const res = await adminFetch(
        `${API_BASE_URL}/api/takeGameStep?adminRemoveAgent=true&agentId=${encodeURIComponent(agent.id)}&mode=${mode}`,
        { method: 'POST' }
      );
      const data = await res.json();
      setResult(data);
      if (data.success) fetchAdminAgents();
    } catch (err) {
      setResult({ success: false, error: err.message });
    }
    setAgentActionId(null);
  };

  const fetchOrSpend = useCallback(async () => {
    if (!adminKey) return;
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminOpenRouterSpend=true`);
      const data = await res.json();
      setOrSpend(data.success ? data : null);
    } catch (err) {
      console.error('Failed to fetch OpenRouter spend:', err);
    }
  }, [adminKey, adminFetch]);

  useEffect(() => { fetchOrSpend(); }, [fetchOrSpend]);

  const fetchSpendAttr = useCallback(async () => {
    if (!adminKey) return;
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminSpendAttribution=true&days=7`);
      const data = await res.json();
      setSpendAttr(data.success ? data : null);
    } catch (err) {
      console.error('Failed to fetch spend attribution:', err);
    }
  }, [adminKey, adminFetch]);

  useEffect(() => { fetchSpendAttr(); }, [fetchSpendAttr]);

  useEffect(() => {
    if (!adminKey) return;
    // Seat IPs from an expanded Humans row are resolved too, not just the
    // list's creator_ip — reading a space's visitor list meant hand-resolving
    // every join_ip elsewhere, which is most of what the column is FOR.
    const seatIps = Object.values(humansByGame).flatMap(list =>
      (list || []).flatMap(p => [p.join_ip, ...(Array.isArray(p.join_ips) ? p.join_ips.map(e => e && e.ip) : [])]));
    const ips = [...new Set([...chatGames.map(g => g.creator_ip), ...seatIps].filter(Boolean))]
      .filter(ip => !(ip in geoByIp) && !((geoRetryAt.current[ip] || 0) > Date.now()))
      .slice(0, 100);
    if (ips.length === 0) return;
    adminFetch(`${API_BASE_URL}/api/takeGameStep?adminGeoIps=true&ips=${encodeURIComponent(ips.join(','))}`)
      .then(r => r.json())
      .then(d => {
        if (!d.success) return;
        // Mark every ANSWERED IP resolved (null when the lookup had nothing)
        // so a miss doesn't refetch on each list refresh. The ones the service
        // didn't really answer (d.unresolved, 09-24) wait 5 minutes instead.
        const unresolved = new Set(Array.isArray(d.unresolved) ? d.unresolved : []);
        unresolved.forEach(ip => { geoRetryAt.current[ip] = Date.now() + 5 * 60 * 1000; });
        setGeoByIp(prev => ({ ...prev, ...Object.fromEntries(ips.filter(ip => !unresolved.has(ip)).map(ip => [ip, d.geo?.[ip] || null])) }));
      })
      .catch(() => { /* geo is decoration; the IP itself still renders */ });
  }, [chatGames, humansByGame, adminKey, adminFetch, geoByIp]);

  const handleSaveKey = () => {
    const trimmed = keyInput.trim();
    if (!trimmed) return;
    localStorage.setItem(ADMIN_KEY_LS, trimmed);
    setAdminKey(trimmed);
    setKeyInput('');
    setResult(null);
  };

  const handleClearKey = () => {
    localStorage.removeItem(ADMIN_KEY_LS);
    setAdminKey('');
    setChatGames([]);
    setResult(null);
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete all ${chatGames.length} chat-game instances?\n\nThey will be backed up to game_instances_backup_2_17_26a first.`)) return;
    setDeleting(true);
    setResult(null);
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminDeleteChatGames=true`, { method: 'POST' });
      const data = await res.json();
      setResult(data);
      if (data.success) fetchChatGames();
    } catch (err) {
      setResult({ success: false, error: err.message });
    }
    setDeleting(false);
  };

  // mode: 'permanent' (backup + delete), 'temporary' (kick + close + unshare),
  // 'restore' (reopen a temporarily-removed space; stays unshared).
  const removeSpace = async (mode) => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      const res = await adminFetch(
        `${API_BASE_URL}/api/takeGameStep?adminRemoveSpace=true&gameId=${encodeURIComponent(removeTarget.id)}&mode=${mode}`,
        { method: 'POST' }
      );
      const data = await res.json();
      setResult(data);
      if (data.success) {
        setRemoveTarget(null);
        fetchChatGames();
      }
    } catch (err) {
      setResult({ success: false, error: err.message });
    }
    setRemoving(false);
  };

  const toggleExpandGame = async (gameId) => {
    if (expandedGameId === gameId) {
      setExpandedGameId(null);
      return;
    }
    setExpandedGameId(gameId);
    if (!humansByGame[gameId]) {
      try {
        const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminListHumanPlayers=true&gameId=${encodeURIComponent(gameId)}`);
        const data = await res.json();
        if (data.success) {
          setHumansByGame(prev => ({ ...prev, [gameId]: data.players || [] }));
        } else {
          setResult(data);
        }
      } catch (err) {
        setResult({ success: false, error: err.message });
      }
    }
  };

  const kickPlayer = async (gameId, playerId) => {
    if (!window.confirm('Kick this player from the space?')) return;
    setKicking(prev => ({ ...prev, [playerId]: true }));
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminKickPlayer=true&playerId=${encodeURIComponent(playerId)}`, { method: 'POST' });
      const data = await res.json();
      setResult(data);
      if (data.success) {
        // Refresh the player list for that game
        setHumansByGame(prev => {
          const { [gameId]: _removed, ...rest } = prev;
          return rest;
        });
        if (expandedGameId === gameId) {
          toggleExpandGame(gameId);
          toggleExpandGame(gameId);
        }
      }
    } catch (err) {
      setResult({ success: false, error: err.message });
    }
    setKicking(prev => ({ ...prev, [playerId]: false }));
  };

  const kickByName = async () => {
    const name = kickName.trim();
    if (!name) return;
    const scopeLabel = kickScope === 'all_games' ? 'ALL spaces' : 'the currently expanded space';
    if (!window.confirm(`Kick every human named "${name}" from ${scopeLabel}?`)) return;
    setResult(null);
    try {
      let url = `${API_BASE_URL}/api/takeGameStep?adminKickByName=true&name=${encodeURIComponent(name)}`;
      if (kickScope === 'current_game' && expandedGameId) {
        url += `&gameId=${encodeURIComponent(expandedGameId)}`;
      }
      const res = await adminFetch(url, { method: 'POST' });
      const data = await res.json();
      setResult(data);
      if (data.success) {
        setKickName('');
        setHumansByGame({});
      }
    } catch (err) {
      setResult({ success: false, error: err.message });
    }
  };

  // 🛡️ Community reports (MODERATION_P1_SPEC) — open flags, most-reported first
  const [reports, setReports] = useState([]);
  const [resolvingTarget, setResolvingTarget] = useState(null);

  const fetchReports = useCallback(async () => {
    if (!adminKey) return;
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminListReports=true`);
      const data = await res.json();
      if (data.success) setReports(data.reports || []);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    }
  }, [adminKey, adminFetch]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const resolveReport = async (targetId, resolution) => {
    setResolvingTarget(targetId);
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminResolveReport=true&targetId=${encodeURIComponent(targetId)}&resolution=${resolution}`, { method: 'POST' });
      const data = await res.json();
      setResult(data);
      if (data.success) fetchReports();
    } catch (err) {
      setResult({ success: false, error: err.message });
    }
    setResolvingTarget(null);
  };

  // 🥚 Campaign egg words (Nina 07-15) — mint /egg magic words for marketing
  // moments without a deploy. The launch trio (dance/crab/tea) is hardcoded.
  const [eggWords, setEggWords] = useState([]);
  const [eggForm, setEggForm] = useState({ word: '', emoji: '🥚', premise: '', card: '' });
  const [eggBusy, setEggBusy] = useState(false);

  const fetchEggWords = useCallback(async () => {
    if (!adminKey) return;
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminListEggWords=true`);
      const data = await res.json();
      if (data.success) setEggWords(data.egg_words || []);
    } catch (err) {
      console.error('Failed to fetch egg words:', err);
    }
  }, [adminKey, adminFetch]);

  useEffect(() => { fetchEggWords(); }, [fetchEggWords]);

  const addEggWord = async () => {
    setEggBusy(true);
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminAddEggWord=true`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eggForm),
      });
      const data = await res.json();
      setResult(data);
      if (data.success) {
        setEggForm({ word: '', emoji: '🥚', premise: '', card: '' });
        fetchEggWords();
      }
    } catch (err) {
      setResult({ success: false, error: err.message });
    }
    setEggBusy(false);
  };

  const deleteEggWord = async (word) => {
    setEggBusy(true);
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminDeleteEggWord=true&word=${encodeURIComponent(word)}`, { method: 'POST' });
      const data = await res.json();
      setResult(data);
      if (data.success) fetchEggWords();
    } catch (err) {
      setResult({ success: false, error: err.message });
    }
    setEggBusy(false);
  };

  const fetchExplorerStatus = useCallback(async () => {
    if (!adminKey) return;
    try {
      const res = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/status`);
      const data = await res.json();
      setExplorerStatus(data);
    } catch (err) {
      setExplorerStatus({ error: err.message });
    }
  }, [adminKey, adminFetch]);

  useEffect(() => { fetchExplorerStatus(); }, [fetchExplorerStatus]);

  // Audience suggest-a-stop satchel (Nina 07-17): viewer-proposed URLs.
  // Read-only list + one-click arm into the custom-URL field — nothing
  // rides without the usual explicit launch.
  const [explorerSuggestions, setExplorerSuggestions] = useState([]);
  useEffect(() => {
    if (!adminKey) return;
    (async () => {
      try {
        const res = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/suggestions`);
        const data = await res.json();
        if (Array.isArray(data?.suggestions)) setExplorerSuggestions(data.suggestions.slice(0, 12));
      } catch (err) { /* satchel is optional */ }
    })();
  }, [adminKey, adminFetch]);

  // Current jukebox opener — shown as the input placeholder — plus the
  // pre-show knob (house song from launch vs classic suit-up theme).
  useEffect(() => {
    if (!adminKey) return;
    adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/jukebox`)
      .then((r) => r.json())
      .then((d) => {
        setJukeboxSeed(d?.seed || null);
        setJukeboxPreshow(d?.preshow === 'theme' ? 'theme' : 'house');
        if (['tts', 'babble', 'both'].includes(d?.voice)) setExplorerVoice(d.voice);
      })
      .catch(() => setJukeboxSeed(null));
  }, [adminKey, adminFetch]);

  // Ballot pool for the destination picker (public endpoint — candidates +
  // live tallies). Best-effort: picker just hides if the fetch fails.
  // GET /explorer/vote nests the payload as {ballot: {..., candidates}} —
  // reading candidates off the top level here meant the picker NEVER
  // rendered (07-16 audit bug 7; "hide on failure" masked the shape
  // mismatch — if this picker ever vanishes again, check this unwrap
  // against the endpoint's response shape first).
  useEffect(() => {
    if (!adminKey) return;
    fetch(`${EXPLORER_API_BASE_URL}/explorer/vote`)
      .then((r) => r.json())
      .then((d) => setExplorerBallot(d?.ballot && Array.isArray(d.ballot.candidates) ? d.ballot : null))
      .catch(() => setExplorerBallot(null));
    // Full pool for the picker (admin endpoint). Best-effort: on failure the
    // picker falls back to the ballot's 4, exactly the pre-07-25 behavior.
    adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/candidates`)
      .then((r) => r.json())
      .then((d) => setExplorerPool(Array.isArray(d?.candidates) && d.candidates.length ? d.candidates : null))
      .catch(() => setExplorerPool(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminKey]);
  // Picker options: the WHOLE pool, with today's ballot tallies stitched on
  // where ids overlap (votes/🏆 only mean something for balloted entries).
  const explorerPickerOptions = (() => {
    const ballotById = {};
    (explorerBallot?.candidates || []).forEach((c) => { ballotById[c.id] = c; });
    const base = explorerPool || explorerBallot?.candidates || [];
    return base.map((c) => ({ ...c, votes: ballotById[c.id]?.votes, onBallot: !!ballotById[c.id] }));
  })();

  // Suggested Briefing card (EXPLORER_REPLAY_SPEC output #2): when the armed
  // destination (custom URL outranks picker, same as launch) matches a host
  // the writer has distilled intel for, offer it next to the textarea.
  const [briefingSuggestion, setBriefingSuggestion] = useState(null);
  const armedDestUrl = explorerUrl.trim()
    || (explorerDest ? (explorerPickerOptions.find((c) => c.id === explorerDest)?.url || '') : '');
  let armedHost = null;
  try {
    armedHost = armedDestUrl
      ? new URL(/^https?:\/\//i.test(armedDestUrl) ? armedDestUrl : `https://${armedDestUrl}`).hostname.toLowerCase()
      : null;
  } catch (e) { armedHost = null; /* half-typed URL — card just stays hidden */ }
  useEffect(() => {
    setBriefingSuggestion(null);
    if (!armedHost || !adminKey) return undefined;
    let gone = false;
    adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/suggestions?host=${encodeURIComponent(armedHost)}`)
      .then((r) => r.json())
      .then((d) => { if (!gone && d?.briefing_suggestion?.text) setBriefingSuggestion(d.briefing_suggestion); })
      .catch(() => { /* card is garnish */ });
    return () => { gone = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armedHost, adminKey]);

  const launchExplorer = async () => {
    const url = explorerUrl.trim();
    // Custom URL outranks the dropdown pick BY DESIGN (paste-a-URL is the
    // operator's escape hatch) — but it must never do so silently: on 07-16
    // a leftover URL in this box re-armed a stale destination past an
    // unsuspecting operator (audit bug 5). The confirm text below calls out
    // the shadowed pick, and the success path clears the box.
    const destCand = explorerDest
      ? explorerPickerOptions.find((c) => c.id === explorerDest)
      : null;
    const where = url
      ? `\n\nDestination: ${url}${destCand ? `\n⚠️ Custom URL wins — your picker choice (${destCand.emoji || ''} ${destCand.label}) will be IGNORED. Clear the URL box to use the picker.` : ''}`
      : destCand
      ? `\n\nDestination: ${destCand.emoji || ''} ${destCand.label}`
      : '\n\nNo expedition — he stays home in the spaces (destinations are opt-in now).';
    const modelNote = explorerModel
      ? `\nModel: ${explorerModel === 'default' ? 'reset to code default (Haiku)' : explorerModel} (sticks for future sessions too)`
      : `\nModel: ${explorerStatus?.model || 'current setting'}`;
    if (!window.confirm(`Launch a live Chibi expedition now? This spins up a real session (ECS + model spend).${where}${modelNote}`)) return;
    setExplorerBusy(true);
    setExplorerResult(null);
    try {
      if (url || destCand) {
        // One-shot override: resolveExpeditionNote consumes it at launch.
        // This is now the ONLY way a destination reaches the bot (07-15).
        const ovr = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/vote/override`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(url ? { url, label: explorerLabel.trim() || url } : { candidate_id: destCand.id }),
            ...(explorerBriefing.trim() ? { briefing: explorerBriefing.trim() } : {}),
          }),
        });
        const ovrData = await ovr.json();
        if (!ovrData.ok) {
          setExplorerResult({ success: false, error: `Override failed: ${JSON.stringify(ovrData)}` });
          setExplorerBusy(false);
          return;
        }
      }
      const res = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(explorerModel ? { model: explorerModel } : {}),
          ...(explorerDuration ? { duration_min: parseInt(explorerDuration, 10) } : {}),
        }),
      });
      const data = await res.json();
      const launched = !data.error && !data.skipped;
      setExplorerResult({ success: launched, ...data });
      setExplorerModel('');
      setExplorerDuration('');
      setExplorerDest('');
      if (launched) {
        // One launch = one destination. Leaving the custom URL in the box
        // re-armed a stale destination on the next launch (07-16 audit
        // bug 5). Kept on failure/skip so the operator can retry as-is.
        // Briefing follows the same one-shot rule.
        setExplorerUrl('');
        setExplorerLabel('');
        setExplorerBriefing('');
      }
      fetchExplorerStatus();
    } catch (err) {
      setExplorerResult({ success: false, error: err.message });
    }
    setExplorerBusy(false);
  };

  const launchAgentExpedition = async () => {
    const agentId = agentLaunchId.trim();
    if (!agentId) return;
    const known = marketplaceAgents.find((a) => a.id === agentId);
    const who = known ? `${known.name} (${agentId.slice(0, 8)}…)` : agentId;
    // Destination copy mirrors the classic launcher: custom URL outranks the
    // picker, and never silently (07-16 audit bug 5 lesson).
    const launchUrl = agentLaunchUrl.trim();
    const destCand = agentLaunchDest ? marketDestPool.find((c) => c.id === agentLaunchDest) : null;
    const whereNote = launchUrl
      ? `\nDestination: ${launchUrl}${destCand ? `\n⚠️ Custom URL wins — picker choice (${destCand.emoji || ''} ${destCand.label}) will be IGNORED.` : ''}`
      : destCand
      ? `\nDestination: ${destCand.emoji || ''} ${destCand.label}`
      : '\nNo destination — wanders the Spaces.';
    if (!window.confirm(`Launch a marketplace expedition for ${who}?\n\nThis spins up a real session (ECS + model spend). Claude floor model, ${agentLaunchDuration} min. Same single body slot as the house chibi.${whereNote}`)) return;
    setAgentLaunchBusy(true);
    setAgentLaunchResult(null);
    try {
      // jsg admin auth wants the X-Admin-Key HEADER (not the query param the
      // explorer-backend endpoints take) — smoke #1/#2 lesson, port spec.
      const res = await fetch(`${API_BASE_URL}/api/agents/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify({
          action: 'launch_explorer',
          agent_id: agentId,
          duration_min: parseInt(agentLaunchDuration, 10),
          ...(launchUrl ? { custom_url: launchUrl } : (agentLaunchDest ? { destination_id: agentLaunchDest } : {})),
        }),
      });
      const data = await res.json();
      // skipped:true (already_live race) is a 200 but NOT a launch — same
      // read as the classic launcher, and the destination fields must
      // survive for the retry (one-shot clear is for real launches only).
      const launched = res.ok && !data.error && !data.skipped;
      setAgentLaunchResult({ success: launched, ...data });
      if (launched) {
        // One launch = one destination (07-16 audit bug 5) — a leftover URL
        // must not re-arm on the next launch.
        setAgentLaunchUrl('');
        setAgentLaunchDest('');
      }
      fetchExplorerStatus();
    } catch (err) {
      setAgentLaunchResult({ success: false, error: err.message });
    }
    setAgentLaunchBusy(false);
  };

  // Approve = LAUNCH NOW (ruling ⑩: Nina is the scheduler). The override
  // pair REPLACES the owner's destination pick when set; custom URL outranks
  // the dropdown, same rule everywhere.
  const approveExpedition = async (entry) => {
    const overrideUrl = queueOverrideUrl.trim();
    const overrideCand = queueOverrideDest ? marketDestPool.find((c) => c.id === queueOverrideDest) : null;
    const ownerPick = entry.destination
      ? `${entry.destination.emoji || ''} ${entry.destination.label || entry.destination.id}`
      : entry.custom_url || 'wanders the Spaces';
    const effective = overrideUrl
      ? `${overrideUrl} (YOUR OVERRIDE)`
      : overrideCand
      ? `${overrideCand.emoji || ''} ${overrideCand.label} (YOUR OVERRIDE)`
      : ownerPick;
    if (!window.confirm(`Approve ${entry.agent_name}'s expedition and launch NOW?\n\nThis spins up a real session (ECS + model spend). ${entry.maiden ? 'FREE FIRST WALK: 5 min on haiku.' : `${entry.duration_min} min, Claude floor model.`}\nDestination: ${effective}\nOwner note: ${entry.note || '(none)'}`)) return;
    setQueueBusyId(entry.id);
    setQueueResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify({
          action: 'approve_expedition',
          request_id: entry.id,
          ...(overrideUrl ? { custom_url: overrideUrl } : (queueOverrideDest ? { destination_id: queueOverrideDest } : {})),
        }),
      });
      const data = await res.json();
      setQueueResult({ success: res.ok && data.success, ...data });
      if (res.ok && data.success) {
        setQueueOverrideUrl('');
        setQueueOverrideDest('');
      }
      fetchExplorerStatus();
      fetchExpeditionQueue();
    } catch (err) {
      setQueueResult({ success: false, error: err.message });
    }
    setQueueBusyId(null);
  };

  const denyExpedition = async (entry) => {
    if (!window.confirm(`Deny ${entry.agent_name}'s expedition request? The owner is not notified (no channel yet) — the entry just leaves the queue.`)) return;
    setQueueBusyId(entry.id);
    setQueueResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify({ action: 'deny_expedition', request_id: entry.id }),
      });
      const data = await res.json();
      setQueueResult({ success: res.ok && data.success, ...data });
      fetchExpeditionQueue();
    } catch (err) {
      setQueueResult({ success: false, error: err.message });
    }
    setQueueBusyId(null);
  };

  const stopExplorer = async () => {
    if (!window.confirm('Stop the live explorer session for everyone?')) return;
    setExplorerBusy(true);
    setExplorerResult(null);
    try {
      const res = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/stop`, { method: 'POST' });
      const data = await res.json();
      setExplorerResult({ success: !data.error, ...data });
      fetchExplorerStatus();
    } catch (err) {
      setExplorerResult({ success: false, error: err.message });
    }
    setExplorerBusy(false);
  };

  // ── Chibi Watch-Along (CHIBI_MATCHDAY_SPEC_2026_07_19.md §4 +
  // CHIBI_WATCH_ALONG_SPEC_2026_07_20.md) ───────────────────────────────────
  // Arms the sidecar commentator in the theater chat. Two event types:
  // sports (Match Day, score-feed eyes) and clock (Movie Night, party clock —
  // chibi can't see the screen, reacts to the room). Fully separate from
  // sessions — works whether or not he's on stage.
  const [matchday, setMatchday] = useState(null);
  const [matchdayBusy, setMatchdayBusy] = useState(false);
  const [matchdayType, setMatchdayType] = useState('sports');
  const [matchdayLeague, setMatchdayLeague] = useState('fifa.world');
  const [matchdayFixture, setMatchdayFixture] = useState('');
  const [movieTitle, setMovieTitle] = useState('');
  const [movieStartIn, setMovieStartIn] = useState('30');
  const [movieRuntime, setMovieRuntime] = useState('120');
  const [movieSubs, setMovieSubs] = useState(null); // {name, text} from the .srt picker
  const fetchMatchdayStatus = useCallback(async () => {
    try {
      const res = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/matchday/status`);
      setMatchday(await res.json());
    } catch (err) { setMatchday({ error: err.message }); }
  }, [adminFetch]);
  useEffect(() => { if (adminKey) fetchMatchdayStatus(); }, [adminKey, fetchMatchdayStatus]);
  const armMatchday = async () => {
    const isClock = matchdayType === 'clock';
    if (isClock && !movieTitle.trim()) { window.alert('Movie night needs a title.'); return; }
    const confirmText = isClock
      ? `Arm chibi for MOVIE NIGHT? He hosts in the theater chat (pre-show ask, start, midpoint, credits, ratings tally) and auto-disarms ~20 min after the runtime ends (small model spend).\n\nTitle: ${movieTitle.trim()}\nStarts in: ${movieStartIn || '0'} min\nRuntime: ${movieRuntime || '120'} min\nSubtitles: ${movieSubs ? movieSubs.name : 'none (ears-first)'}`
      : `Arm chibi as live match commentator? He posts in the theater chat every match beat until ~20 min after full time (small model spend).\n\nLeague: ${matchdayLeague}${matchdayFixture.trim() ? `\nFixture: ${matchdayFixture.trim()}` : '\nFixture: auto-pick today\'s match'}`;
    if (!window.confirm(confirmText)) return;
    setMatchdayBusy(true);
    try {
      const body = isClock
        ? { event_type: 'clock', title: movieTitle.trim(), start_in_min: parseInt(movieStartIn, 10) || 0, runtime_min: parseInt(movieRuntime, 10) || 120, ...(movieSubs ? { subtitle_srt: movieSubs.text } : {}) }
        : { league: matchdayLeague, ...(matchdayFixture.trim() ? { fixture_id: matchdayFixture.trim() } : {}) };
      const res = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/matchday/arm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.ok) { setMatchdayFixture(''); if (isClock) { setMovieTitle(''); setMovieSubs(null); } }
      if (data.subs_error) window.alert(`Armed, but subtitles failed to parse (${data.subs_error}) — running ears-first.`);
      await fetchMatchdayStatus();
      if (!data.ok) setMatchday((prev) => ({ ...(prev || {}), error: JSON.stringify(data) }));
    } catch (err) { setMatchday({ error: err.message }); }
    setMatchdayBusy(false);
  };
  const disarmMatchday = async () => {
    if (!window.confirm('Disarm the match commentator? He goes quiet in chat right away.')) return;
    setMatchdayBusy(true);
    try {
      await adminFetch(`${EXPLORER_API_BASE_URL}/admin/matchday/disarm`, { method: 'POST' });
      await fetchMatchdayStatus();
    } catch (err) { setMatchday({ error: err.message }); }
    setMatchdayBusy(false);
  };

  const pageShell = { minHeight: '100vh', background: '#0d1117', color: '#dde3ea', fontFamily: FONT_UI, fontSize: 14, lineHeight: 1.5 };

  if (!adminKey) {
    return (
      <div style={pageShell}>
        <div style={{ maxWidth: 420, margin: '0 auto', padding: '110px 20px 0' }}>
          <h1 style={{ color: '#fff', fontSize: 24, margin: '0 0 6px' }}>🔐 Admin</h1>
          <p style={{ ...S.blurb, marginBottom: 18 }}>Enter the admin API key to continue. It's stored in this browser only.</p>
          <input
            type="password"
            value={keyInput}
            onChange={e => setKeyInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSaveKey()}
            placeholder="Admin API key"
            style={{ ...S.input, width: '100%', boxSizing: 'border-box', padding: 12, fontSize: 15 }}
          />
          <Btn kind="blue" onClick={handleSaveKey} style={{ marginTop: 12, width: '100%', padding: 12, fontSize: 15 }}>
            Unlock
          </Btn>
          <ResultPane data={result} onDismiss={() => setResult(null)} />
        </div>
      </div>
    );
  }

  const gameStatus = (g) => g.game_completed
    ? { tone: 'idle', text: 'Completed' }
    : g.is_playing ? { tone: 'ok', text: 'Playing' } : { tone: 'idle', text: 'Idle' };

  return (
    <div style={pageShell}>
      {/* Sticky section nav — the page is 6 unrelated tool groups; jumping
          beats scroll-hunting. */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'rgba(13,17,23,0.94)', backdropFilter: 'blur(6px)', borderBottom: '1px solid #26303f' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', padding: '10px 20px', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ color: '#fff', fontSize: 15, marginRight: 8 }}>Admin</strong>
          {NAV.map(([id, label]) => (
            <a key={id} href={`#${id}`} style={{ padding: '4px 10px', borderRadius: 999, fontSize: 12.5, color: '#b9c4d4', background: '#1b2330', textDecoration: 'none', whiteSpace: 'nowrap' }}>
              {label}
            </a>
          ))}
          <span style={{ flex: 1 }} />
          <Btn onClick={handleClearKey} style={{ padding: '4px 10px', fontSize: 12 }}>Sign out</Btn>
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '20px 20px 80px' }}>
        {/* Global action feedback — was buried at the very bottom of the page. */}
        {result && <div style={{ marginBottom: 16 }}><ResultPane data={result} onDismiss={() => setResult(null)} /></div>}

        <Card
          id="explorer"
          accent="#2e7d32"
          title="🦀 Bot the Explorer"
          blurb="Launch a live Chibi expedition. Expeditions are opt-in: arm a destination (picker or custom URL) or he stays home in the spaces. The audience vote still runs — its winner is only a suggestion for you."
        >
          <p style={{ ...S.blurb, marginBottom: 12 }}>
            {explorerStatus?.error
              ? <Pill tone="warn">Status unavailable: {explorerStatus.error}</Pill>
              : explorerStatus
              ? (explorerStatus.is_live
                ? <Pill tone="live">● LIVE right now{explorerStatus.agent_name ? ` — 🛂 ${explorerStatus.agent_name}` : ''}</Pill>
                : <>
                    <Pill tone="idle">Offline</Pill>
                    {' '}Next scheduled show: {explorerStatus.next_live_at ? new Date(explorerStatus.next_live_at).toLocaleString() : 'unknown'} — auto-launch cron ON (re-enabled 07-24, daily 00:00 UTC / 8pm EDT). Manual launches still work anytime.
                  </>)
              : 'Checking status…'}
            {explorerStatus && !explorerStatus.error && (
              <>{' '}Session model: <code style={{ fontFamily: FONT_MONO, color: '#a9e8b2' }}>{explorerStatus.model || 'default'}</code>{explorerStatus.model === 'default' || !explorerStatus.model ? ' (Haiku 4.5)' : ''} — picking one below sticks for scheduled sessions too.</>
            )}
          </p>

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
            {explorerPickerOptions.length > 0 && (
              <Field label="Destination (full pool · 🗳️ = on today's ballot)">
                <select
                  value={explorerDest}
                  onChange={e => setExplorerDest(e.target.value)}
                  title="Expeditions are opt-in: pick any pool destination to arm it for this launch (ballot entries show live votes, winner marked). Blank = he stays home."
                  style={{ ...S.input, minWidth: 220 }}
                >
                  <option value="">🏠 No expedition (stays home)</option>
                  {explorerPickerOptions.map((c) => {
                    const balloted = explorerPickerOptions.filter((o) => o.onBallot);
                    const isLeader = c.onBallot && balloted.every((o) => (o.votes || 0) <= (c.votes || 0)) && (c.votes || 0) > 0;
                    return (
                      <option key={c.id} value={c.id}>
                        {c.emoji} {c.label}{c.onBallot ? ` 🗳️${typeof c.votes === 'number' ? ` ${c.votes} vote${c.votes === 1 ? '' : 's'}` : ''}` : ''}{isLeader ? ' 🏆' : ''}
                      </option>
                    );
                  })}
                </select>
              </Field>
            )}
            <Field label="Custom URL (overrides the picker)" style={{ flex: 2, minWidth: 260 }}>
              <input
                type="text"
                value={explorerUrl}
                onChange={e => setExplorerUrl(e.target.value)}
                placeholder="https://…"
                style={S.input}
              />
            </Field>
            <Field label="Label (optional)" style={{ flex: 1, minWidth: 140 }}>
              <input
                type="text"
                value={explorerLabel}
                onChange={e => setExplorerLabel(e.target.value)}
                placeholder="Shown to viewers"
                style={S.input}
              />
            </Field>
          </div>

          <Field label="Field briefing (optional) — rides the bot's note-to-self" style={{ marginBottom: 12 }}>
            <textarea
              value={explorerBriefing}
              onChange={e => setExplorerBriefing(e.target.value)}
              maxLength={400}
              rows={2}
              placeholder="Quirks to expect, what to skip, what to go see. e.g. 'The conjure menu is buggy in pre-alpha, one try max. The strip at night is the real show, go walk it.'"
              title="Rides the armed destination as part of the bot's note-to-self (works with picker or custom URL)"
              style={{ ...S.input, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </Field>

          {/* 💡 Suggested Briefing (EXPLORER_REPLAY_SPEC output #2): the Claw
              Cut writer distills per-site intel from his own struggles,
              keyed by destination host. The model DRAFTS, the operator
              approves — [Use] only fills the editable textarea, NOTHING
              auto-attaches. Dismiss is per-render on purpose: the note is
              cheap to re-read and hiding it server-side could bury a
              still-relevant lesson. */}
          {briefingSuggestion && briefingSuggestion.text && (
            <div style={{ marginBottom: 12, padding: '8px 12px', background: '#132018', border: '1px dashed #4a7', borderRadius: 6, fontSize: 12.5, color: '#cfe8d6', lineHeight: 1.45 }}>
              💡 Suggested briefing{briefingSuggestion.episode_title ? ` (from "${briefingSuggestion.episode_title}"${briefingSuggestion.generated_at ? `, ${new Date(briefingSuggestion.generated_at).toLocaleDateString()}` : ''})` : ''}: {briefingSuggestion.text}
              <span style={{ whiteSpace: 'nowrap', marginLeft: 8 }}>
                <Btn kind="primary" onClick={() => setExplorerBriefing(briefingSuggestion.text)} style={{ padding: '2px 10px', marginRight: 6, fontSize: 12, fontWeight: 400 }}>
                  Use
                </Btn>
                <Btn onClick={() => setBriefingSuggestion(null)} style={{ padding: '2px 10px', fontSize: 12, background: 'transparent', border: '1px solid #39455a', color: '#9aa7b8' }}>
                  Dismiss
                </Btn>
              </span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Field label="Model">
              <select
                value={explorerModel}
                onChange={e => setExplorerModel(e.target.value)}
                title="Model for this launch — sticks for future scheduled sessions until changed"
                style={{ ...S.input, minWidth: 190 }}
              >
                <option value="">Keep current</option>
                <optgroup label="Claude (house models)">
                  <option value="openrouter/anthropic/claude-haiku-4.5">Haiku 4.5 (cheap)</option>
                  <option value="openrouter/anthropic/claude-sonnet-4.6">Sonnet 4.6 (3x cost, better prose)</option>
                  <option value="openrouter/anthropic/claude-opus-4.8">Opus 4.8 (5x cost, big brain)</option>
                </optgroup>
                {/* Guest stars: non-Anthropic primaries. Two caveats the labels
                    can't hold: (1) OpenClaw context pruning is Anthropic-specific,
                    so these NEVER prune — screenshot-heavy game trips bloat the
                    context all session (big-ctx models tolerate it, wallets less
                    so). (2) Vision matters: canvas games are DOM-blind, so a
                    text-only model has no eyes on game expeditions — keep
                    DeepSeek at home in the spaces. Ids verified live against
                    openrouter.ai/api/v1/models 07-17. */}
                <optgroup label="Guest stars (never prune context; watch first run)">
                  <option value="openrouter/google/gemini-3.5-flash">Gemini 3.5 Flash (vision, 1M ctx, ~Sonnet cost)</option>
                  <option value="openrouter/google/gemini-3.1-flash-lite">Gemini 3.1 Flash Lite (cheap, vision, 1M ctx)</option>
                  <option value="openrouter/openai/gpt-5.1">GPT-5.1 (vision, ~Opus cost)</option>
                  <option value="openrouter/x-ai/grok-4.3">Grok 4.3 (vision, 1M ctx, wildcard)</option>
                  <option value="openrouter/moonshotai/kimi-k2.5">Kimi K2.5 (cheap, vision, agentic)</option>
                  <option value="openrouter/deepseek/deepseek-v4-pro">DeepSeek V4 Pro (dirt cheap, TEXT-ONLY: no game trips)</option>
                </optgroup>
                <option value="default">Reset to code default</option>
              </select>
            </Field>
            <Field label="Session length">
              <select
                value={explorerDuration}
                onChange={e => setExplorerDuration(e.target.value)}
                title="Bot ACTIVE minutes for THIS launch only (not sticky). A picked length runs the kick loop on a real clock from bot-ready, so this is what you actually watch; default keeps classic iteration pacing. Longer sessions risk context bloat — 30 is the ceiling on purpose."
                style={S.input}
              >
                <option value="">Default (classic pacing)</option>
                <option value="20">20 min active</option>
                <option value="30">30 min active (bloat risk)</option>
              </select>
            </Field>
            <Btn
              kind={explorerModel ? 'blue' : 'subtle'}
              onClick={async () => {
                if (!explorerModel) return;
                setExplorerBusy(true);
                try {
                  const res = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/start`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ model: explorerModel, model_only: true }),
                  });
                  const data = await res.json();
                  setExplorerResult({ success: !!data.ok, ...data });
                  setExplorerModel('');
                  fetchExplorerStatus();
                } catch (err) {
                  setExplorerResult({ success: false, error: err.message });
                }
                setExplorerBusy(false);
              }}
              disabled={explorerBusy || !explorerModel}
              title="Save the model choice for future sessions WITHOUT launching now"
            >
              💾 Set model
            </Btn>
            <Btn
              kind="primary"
              onClick={launchExplorer}
              disabled={explorerBusy || explorerStatus?.is_live}
              title={explorerStatus?.is_live ? 'Already live — stop the current session first' : 'Launch a live expedition now'}
              style={{ padding: '8px 18px' }}
            >
              {explorerBusy ? 'Working…' : '🚀 Launch Chibi'}
            </Btn>
            <Btn
              kind="danger"
              onClick={stopExplorer}
              disabled={explorerBusy || (explorerStatus && !explorerStatus.is_live)}
            >
              ⏹ Stop
            </Btn>
            <Btn onClick={fetchExplorerStatus} disabled={explorerBusy} title="Refresh status">
              ↻
            </Btn>
          </div>
          <ResultPane data={explorerResult} onDismiss={() => setExplorerResult(null)} />

          {/* 🛂 Marketplace expedition — the ported-agent launcher. Separate
              row on purpose: different backend service, different knobs (no
              model picker — the Claude floor pins it). 08-12: destinations
              landed (queue handoff §3/§5) — the wander-only note above this
              was retired with them. */}
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #26303f' }}>
            <div style={{ fontSize: 13, color: '#a9e8b2', marginBottom: 6 }}>
              🛂 Marketplace expedition — launch a ported agent in the chibi body. Claude floor model. Private agents never show in the dropdown: paste the agent id (ruling ⑫ — this launcher bypasses the queue).
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              {marketplaceAgents.length > 0 && (
                <Field label="Public agents">
                  <select
                    value={marketplaceAgents.some((a) => a.id === agentLaunchId) ? agentLaunchId : ''}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      setAgentLaunchId(e.target.value);
                      try { localStorage.setItem('adminAgentLaunchId', e.target.value); } catch { /* private mode */ }
                    }}
                    style={{ ...S.input, minWidth: 200 }}
                  >
                    <option value="">Pick an agent…</option>
                    {marketplaceAgents.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label="Agent id (works for private agents)" style={{ flex: 2, minWidth: 280 }}>
                <input
                  type="text"
                  value={agentLaunchId}
                  onChange={(e) => {
                    setAgentLaunchId(e.target.value);
                    try { localStorage.setItem('adminAgentLaunchId', e.target.value); } catch { /* private mode */ }
                  }}
                  placeholder="agent_registrations id (uuid)"
                  style={{ ...S.input, fontFamily: FONT_MONO }}
                />
              </Field>
              <Field label="Session length">
                <select
                  value={agentLaunchDuration}
                  onChange={(e) => setAgentLaunchDuration(e.target.value)}
                  style={S.input}
                >
                  <option value="10">10 min</option>
                  <option value="15">15 min</option>
                  <option value="20">20 min</option>
                  <option value="30">30 min (bloat risk)</option>
                </select>
              </Field>
              {/* Destination pair copied from the classic launcher (queue
                  handoff §2): custom URL outranks the picker, one-shot. */}
              <Field label="Destination (optional)">
                <select
                  value={agentLaunchDest}
                  onChange={(e) => setAgentLaunchDest(e.target.value)}
                  style={{ ...S.input, minWidth: 170 }}
                >
                  <option value="">Wander the Spaces</option>
                  {marketDestPool.map((c) => (
                    <option key={c.id} value={c.id}>{c.emoji || ''} {c.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Custom URL (outranks picker)" style={{ minWidth: 200 }}>
                <input
                  type="text"
                  value={agentLaunchUrl}
                  onChange={(e) => setAgentLaunchUrl(e.target.value)}
                  placeholder="https://…"
                  style={{ ...S.input, fontFamily: FONT_MONO }}
                />
              </Field>
              <Btn
                kind="primary"
                onClick={launchAgentExpedition}
                disabled={agentLaunchBusy || explorerBusy || explorerStatus?.is_live || !agentLaunchId.trim()}
                title={explorerStatus?.is_live ? 'Already live — one body; stop the current session first' : 'Launch this agent now'}
                style={{ padding: '8px 18px' }}
              >
                {agentLaunchBusy ? 'Working…' : '🛂 Launch Agent'}
              </Btn>
            </div>
            <ResultPane data={agentLaunchResult} onDismiss={() => setAgentLaunchResult(null)} />

            {/* 🛫 Pending expedition requests (queue handoff §5.6): owners
                request from /agents/dashboard, each approval launches NOW
                (ruling ⑩). Approve is disabled while a session is live —
                one body. The override pair REPLACES the owner's pick. */}
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, color: '#a9e8b2', marginBottom: 6 }}>
                🛫 Expedition requests ({expeditionQueue.length})
                {expeditionQueueLive ? ' — a session is LIVE, approvals wait for the slot' : ''}
                <Btn onClick={fetchExpeditionQueue} title="Refresh the queue" style={{ marginLeft: 8, padding: '2px 8px', fontSize: 12 }}>↻</Btn>
              </div>
              {expeditionQueue.length === 0 ? (
                <div style={{ fontSize: 12.5, color: '#8b98a9' }}>No pending requests.</div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 8 }}>
                    <Field label="Override destination (optional — replaces the owner's pick on Approve)">
                      <select
                        value={queueOverrideDest}
                        onChange={(e) => setQueueOverrideDest(e.target.value)}
                        style={{ ...S.input, minWidth: 200 }}
                      >
                        <option value="">Keep the owner's pick</option>
                        {marketDestPool.map((c) => (
                          <option key={c.id} value={c.id}>{c.emoji || ''} {c.label}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Override custom URL (outranks dropdown)" style={{ flex: 1, minWidth: 220 }}>
                      <input
                        type="text"
                        value={queueOverrideUrl}
                        onChange={(e) => setQueueOverrideUrl(e.target.value)}
                        placeholder="https://…"
                        style={{ ...S.input, fontFamily: FONT_MONO }}
                      />
                    </Field>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {expeditionQueue.map((entry) => (
                      <div key={entry.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, padding: '6px 8px', background: 'rgba(255,255,255,0.03)', borderRadius: 6 }}>
                        {entry.agent_avatar_url
                          ? <img src={entry.agent_avatar_url} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover' }} />
                          : <span>🛂</span>}
                        <span style={{ fontWeight: 700, color: '#dfe6f2' }}>{entry.agent_name}</span>
                        {entry.maiden && <span title="Free first walk: 5 min, haiku">🐣 first walk</span>}
                        <span style={{ color: '#8b98a9' }}>{entry.duration_min} min</span>
                        <span style={{ color: '#c4cede' }}>
                          → {entry.destination
                            ? `${entry.destination.emoji || ''} ${entry.destination.label || entry.destination.id}`
                            : (entry.custom_url || 'wanders the Spaces')}
                        </span>
                        {entry.note && <span style={{ color: '#8b98a9', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }} title={entry.note}>“{entry.note}”</span>}
                        {entry.is_public_agent === false && <span style={{ color: '#e8a9a9' }} title="Should not happen — private agents are rejected at request">⚠️ private</span>}
                        <span style={{ color: '#66707f', fontSize: 11.5 }}>{entry.requested_at ? new Date(entry.requested_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</span>
                        <span style={{ flex: 1 }} />
                        <Btn
                          kind="primary"
                          onClick={() => approveExpedition(entry)}
                          disabled={queueBusyId === entry.id || expeditionQueueLive || explorerStatus?.is_live}
                          title={expeditionQueueLive || explorerStatus?.is_live ? 'A session is live — one body; approve after it ends' : 'Approve and launch NOW (real spend)'}
                          style={{ padding: '4px 12px', fontSize: 12 }}
                        >
                          {queueBusyId === entry.id ? '…' : '✅ Approve'}
                        </Btn>
                        <Btn
                          onClick={() => denyExpedition(entry)}
                          disabled={queueBusyId === entry.id}
                          title="Remove from the queue (owner is not notified — no channel yet)"
                          style={{ padding: '4px 12px', fontSize: 12 }}
                        >
                          ❌ Deny
                        </Btn>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <ResultPane data={queueResult} onDismiss={() => setQueueResult(null)} />
            </div>
          </div>

          {explorerSuggestions.length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #26303f' }}>
              <div style={{ fontSize: 13, color: '#a9e8b2', marginBottom: 6 }}>
                🎒 Audience suggestions ({explorerSuggestions.length}) — Arm fills the custom-URL field; launch stays on you.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {explorerSuggestions.map((s, i) => (
                  <div key={`${s.ts || i}`} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
                    <Btn kind="blue" onClick={() => { setExplorerUrl(s.url); setExplorerLabel(''); }} style={{ padding: '3px 10px', fontSize: 12, flexShrink: 0 }}>
                      Arm
                    </Btn>
                    <span style={{ color: '#c4cede', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: FONT_MONO }}>{s.url}</span>
                    <span style={{ color: '#8b98a9', flexShrink: 0 }}>{s.ts ? new Date(s.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card
          id="show"
          accent="#7b5cc6"
          title="🎛 Show controls"
          blurb="Sound of the live show — the jukebox opener and what the crab's voice sounds like for every viewer."
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {/* Priority model (Nina 07-19, v2): the house song OPENS every
                show (head of queue at showtime; a fresh mid-song audience
                track finishes first). The audience queue plays right after —
                tonight's pre-show picks survive, entries older than 2h are
                pruned at curtain (past-session leftovers were outranking the
                house song). Skip / Play-next stay the explicit override. */}
            <Field
              label="🎵 Jukebox house song (opens every show)"
              style={{ flex: 2, minWidth: 260 }}
            >
              <input
                type="text"
                value={jukeboxUrl}
                onChange={e => setJukeboxUrl(e.target.value)}
                placeholder={jukeboxSeed ? `${jukeboxSeed.default ? 'Default' : 'Current'}: youtu.be/${jukeboxSeed.video_id}` : 'YouTube link'}
                title="Opens every show, then the audience queue (viewer 🎵 Music tab) takes over. Pre-show picks within 2h of curtain survive; older leftovers are pruned at showtime."
                style={S.input}
              />
            </Field>
            <Btn
              kind={jukeboxUrl.trim() ? 'blue' : 'subtle'}
              onClick={async () => {
                const url = jukeboxUrl.trim();
                if (!url) return;
                setJukeboxBusy(true); setJukeboxMsg('');
                try {
                  const res = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/jukebox`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ url }),
                  });
                  const data = await res.json();
                  if (data.ok) { setJukeboxSeed(data.seed); setJukeboxUrl(''); setJukeboxMsg(`Saved — opens every show with youtu.be/${data.seed.video_id}`); }
                  else setJukeboxMsg(data.hint || data.reason || 'Save failed');
                } catch (err) { setJukeboxMsg(err.message); }
                setJukeboxBusy(false);
              }}
              disabled={jukeboxBusy || !jukeboxUrl.trim()}
            >
              💾 Set house song
            </Btn>
            <Btn
              kind={jukeboxUrl.trim() ? 'warn' : 'subtle'}
              onClick={async () => {
                const url = jukeboxUrl.trim();
                if (!url) return;
                setJukeboxBusy(true); setJukeboxMsg('');
                try {
                  const res = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/jukebox`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'play_next', url }),
                  });
                  const data = await res.json();
                  if (data.ok) { setJukeboxUrl(''); setJukeboxMsg('Jumped the queue — plays after the current song (queued as "the management 🎬")'); }
                  else setJukeboxMsg(data.hint || data.reason || 'Play-next failed');
                } catch (err) { setJukeboxMsg(err.message); }
                setJukeboxBusy(false);
              }}
              disabled={jukeboxBusy || !jukeboxUrl.trim()}
              title="Push this URL to the FRONT of the audience queue, visibly labeled as the management"
            >
              ⏫ Play next
            </Btn>
            <Btn
              kind="danger"
              onClick={async () => {
                if (!window.confirm('Skip the current song for everyone?')) return;
                setJukeboxBusy(true); setJukeboxMsg('');
                try {
                  const res = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/jukebox`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'skip' }),
                  });
                  const data = await res.json();
                  if (data.ok) setJukeboxMsg(data.now_playing ? 'Skipped — next song is up' : 'Skipped — queue is empty, lofi bed takes over');
                } catch (err) { setJukeboxMsg(err.message); }
                setJukeboxBusy(false);
              }}
              disabled={jukeboxBusy}
              title="End the currently playing song for every viewer; the queue advances"
            >
              ⏭ Skip current
            </Btn>
            <Btn
              onClick={async () => {
                setJukeboxBusy(true); setJukeboxMsg('');
                try {
                  const res = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/jukebox`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ clear: true }),
                  });
                  const data = await res.json();
                  // There is no "no opener" anymore — clearing reverts to the
                  // baked default house song the server sends back.
                  if (data.ok) { setJukeboxSeed(data.seed || null); setJukeboxMsg(data.seed ? `Back to the default house song (youtu.be/${data.seed.video_id})` : 'Opener cleared'); }
                } catch (err) { setJukeboxMsg(err.message); }
                setJukeboxBusy(false);
              }}
              disabled={jukeboxBusy || !jukeboxSeed || jukeboxSeed.default === true}
              title="Drop the custom opener and go back to the baked default house song"
            >
              ✕ Clear
            </Btn>
            <Btn
              onClick={async () => {
                const next = jukeboxPreshow === 'theme' ? 'house' : 'theme';
                setJukeboxBusy(true); setJukeboxMsg('');
                try {
                  const res = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/jukebox`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ preshow: next }),
                  });
                  const data = await res.json();
                  if (data.ok) {
                    setJukeboxPreshow(data.preshow);
                    setJukeboxMsg(data.preshow === 'theme' ? 'Pre-show: suit-up theme plays through boot, house song joins with the show' : 'Pre-show: house song plays the moment a show launches');
                  } else setJukeboxMsg(data.reason || 'Toggle failed');
                } catch (err) { setJukeboxMsg(err.message); }
                setJukeboxBusy(false);
              }}
              disabled={jukeboxBusy}
              title="What plays while the crab boots: the house song from the moment of launch (default), or the classic suit-up theme with the house song joining when the show is up"
            >
              {jukeboxPreshow === 'theme' ? '🎺 Pre-show: suit-up theme' : '🎵 Pre-show: house song'}
            </Btn>
          </div>
          {jukeboxMsg && <div style={{ marginTop: 8, fontSize: 12.5, color: '#9ec5e8' }}>{jukeboxMsg}</div>}

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 16, paddingTop: 14, borderTop: '1px solid #26303f' }}>
            {/* Voice knob (07-20): default for every viewer without an explicit
                ?voice= URL. Takes effect on the next viewer load; flipping it
                mid-show reloads theater viewers' iframe (few-second blip).
                Volumes/cadence identical across modes — regular-session
                settings, never the watch-party specials. */}
            <Field label="🎤 Chibi voice (for every viewer)">
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} title="Spoken = real voice on captions and big lines (the default; watch-alongs auto-switch to Both while an event is armed). Babble = classic animalese. Both = babble the play-by-play, spoken voice for the big lines (opener, mission, journal, verdict).">
                {[
                  ['tts', '🎙 Spoken'],
                  ['babble', '🦀 Babble'],
                  ['both', '🎙+🦀 Both'],
                ].map(([mode, label]) => (
                  <Btn
                    key={mode}
                    onClick={async () => {
                      setJukeboxBusy(true); setExplorerVoiceMsg('');
                      try {
                        const res = await adminFetch(`${EXPLORER_API_BASE_URL}/admin/explorer/jukebox`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ voice: mode }),
                        });
                        const data = await res.json();
                        if (data.ok) {
                          setExplorerVoice(data.voice);
                          setExplorerVoiceMsg(data.voice === 'tts' ? 'Spoken voice for everyone' : data.voice === 'babble' ? 'Classic babble for everyone' : 'Babble the captions, spoken voice for the big moments');
                        } else setExplorerVoiceMsg(data.reason || 'Voice set failed');
                      } catch (err) { setExplorerVoiceMsg(err.message); }
                      setJukeboxBusy(false);
                    }}
                    disabled={jukeboxBusy}
                    kind={explorerVoice === mode ? 'blue' : 'subtle'}
                    style={explorerVoice === mode ? { border: '1px solid #4a9ac8' } : { border: '1px solid #39455a' }}
                  >
                    {label}
                  </Btn>
                ))}
              </div>
            </Field>
            {explorerVoiceMsg && <span style={{ fontSize: 12.5, color: '#9ec5e8', paddingBottom: 8 }}>{explorerVoiceMsg}</span>}
          </div>
        </Card>

        <Card
          id="watch"
          accent="#2b6cb0"
          title="📺 Chibi Watch-Along"
          blurb="Arms chibi as co-watch host in the theater chat. Match Day: live commentator off the score feed (goal bursts, halftime takes, prediction winners). Movie Night: party-clock host (pre-show ask, start, midpoint, credits, ratings tally) — he can't see the screen and reacts to the room. Needs no live session, auto-disarms ~20 min after the event ends."
        >
          <p style={{ ...S.blurb, marginBottom: 12 }}>
            {matchday?.error
              ? <Pill tone="warn">Status error: {String(matchday.error).slice(0, 120)}</Pill>
              : matchday?.cfg?.armed
              ? <>
                  <Pill tone="ok">ARMED</Pill>
                  <strong style={{ color: '#a9e8b2' }}>{matchday.match?.event_type === 'clock'
                    ? ` ${matchday.match.label} "${matchday.match.title}" (${matchday.match.phase === 'pre' ? `starts in ${matchday.match.mins_to_start} min` : matchday.match.phase === 'post' ? 'credits rolled' : `${matchday.match.elapsed_min}/${matchday.match.runtime_min} min`})${matchday.cfg?.subs_cues ? ` · 📜 ${matchday.cfg.subs_cues} cues` : ''}`
                    : matchday.match?.home ? ` ${matchday.match.home} ${matchday.match.home_score} - ${matchday.match.away_score} ${matchday.match.away} (${matchday.match.phase}${matchday.match.clock ? ` ${matchday.match.clock}` : ''})` : ''}</strong>
                </>
              : matchday ? <Pill tone="idle">Not armed</Pill> : 'Checking…'}
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Field label="Event type">
              <select
                value={matchdayType}
                onChange={e => setMatchdayType(e.target.value)}
                style={S.input}
              >
                <option value="sports">⚽ Match Day</option>
                <option value="clock">🎬 Movie Night</option>
              </select>
            </Field>
            {matchdayType === 'sports' ? (<>
            <Field label="League">
              <select
                value={matchdayLeague}
                onChange={e => setMatchdayLeague(e.target.value)}
                style={S.input}
              >
                <option value="fifa.world">World Cup</option>
                <option value="eng.1">Premier League</option>
                <option value="usa.1">MLS</option>
                <option value="uefa.champions">Champions League</option>
              </select>
            </Field>
            <Field label="Fixture id (blank = today's match)">
              <input
                value={matchdayFixture}
                onChange={e => setMatchdayFixture(e.target.value)}
                placeholder="auto-pick"
                style={{ ...S.input, width: 200 }}
              />
            </Field>
            </>) : (<>
            <Field label="Movie title">
              <input
                value={movieTitle}
                onChange={e => setMovieTitle(e.target.value)}
                placeholder='e.g. The Thing (1982)'
                style={{ ...S.input, width: 220 }}
              />
            </Field>
            <Field label="Starts in (min)">
              <input
                value={movieStartIn}
                onChange={e => setMovieStartIn(e.target.value.replace(/\D/g, ''))}
                style={{ ...S.input, width: 70 }}
              />
            </Field>
            <Field label="Runtime (min)">
              <input
                value={movieRuntime}
                onChange={e => setMovieRuntime(e.target.value.replace(/\D/g, ''))}
                style={{ ...S.input, width: 70 }}
              />
            </Field>
            <label style={{ fontSize: 13, color: '#9aa7b8', cursor: 'pointer', paddingBottom: 8 }}>
              📜 {movieSubs ? movieSubs.name : 'subs (.srt, optional)'}
              <input
                type="file"
                accept=".srt,.vtt"
                style={{ display: 'none' }}
                onChange={e => {
                  const f = e.target.files && e.target.files[0];
                  if (!f) { setMovieSubs(null); return; }
                  f.text().then(text => setMovieSubs({ name: f.name, text })).catch(() => setMovieSubs(null));
                  e.target.value = '';
                }}
              />
            </label>
            {movieSubs && (
              <Btn onClick={() => setMovieSubs(null)} style={{ padding: '4px 8px', fontSize: 11 }}>
                ✕ subs
              </Btn>
            )}
            </>)}
            <Btn kind="primary" onClick={armMatchday} disabled={matchdayBusy}>
              {matchdayType === 'clock' ? '🎬 Arm movie night' : '⚽ Arm commentator'}
            </Btn>
            <Btn kind="danger" onClick={disarmMatchday} disabled={matchdayBusy || !matchday?.cfg?.armed}>
              Disarm
            </Btn>
            <Btn onClick={fetchMatchdayStatus} disabled={matchdayBusy}>
              ↻ Refresh
            </Btn>
          </div>
        </Card>

        <Card
          id="reports"
          accent="#b23b3b"
          title={`🛡️ Reports (${reports.length})`}
          blurb="Community reports, most-reported first. 3+ distinct reporters auto-hides messages and pulls watch-party videos; streams are flagged here for a human call (kick tools below)."
        >
          {reports.length === 0 ? (
            <p style={{ color: '#8b98a9', fontSize: 13, margin: 0 }}>No open reports. 🎉</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #39455a' }}>
                    <th style={S.th}>Kind</th>
                    <th style={S.th}>Target</th>
                    <th style={S.th}>Reporters</th>
                    <th style={S.th}>First reported</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map(r => (
                    <tr key={r.target_id} style={{ borderBottom: '1px solid #222b38' }}>
                      <td style={S.td}>{r.kind}</td>
                      <td style={{ ...S.td, fontFamily: FONT_MONO, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#a9b6c8' }}>{r.target_id}</td>
                      <td style={{ ...S.td, fontWeight: 700, color: r.reporters >= 3 ? '#ff7b7b' : '#dde3ea' }}>{r.reporters}</td>
                      <td style={S.td}>{new Date(r.first_reported_at).toLocaleString()}</td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                        <Btn
                          onClick={() => resolveReport(r.target_id, 'dismissed')}
                          disabled={resolvingTarget === r.target_id}
                          style={{ padding: '4px 10px', fontSize: 12, marginRight: 6 }}
                        >
                          Dismiss + unhide
                        </Btn>
                        <Btn
                          kind="danger"
                          onClick={() => resolveReport(r.target_id, 'upheld')}
                          disabled={resolvingTarget === r.target_id}
                          style={{ padding: '4px 10px', fontSize: 12 }}
                        >
                          Uphold (keep hidden)
                        </Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          id="eggs"
          accent="#b08a2e"
          title={`🥚 Egg words (${eggWords.length})`}
          blurb="Campaign magic words for /egg. Mint one per marketing moment, no deploy. People who saw the post whisper it in any free-chat space and the room reacts. dance / crab / tea are built in."
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
            <Field label="Word">
              <input
                placeholder="e.g. moonpie"
                value={eggForm.word}
                onChange={e => setEggForm(f => ({ ...f, word: e.target.value }))}
                style={{ ...S.input, width: 130 }}
              />
            </Field>
            <Field label="Emoji">
              <input
                placeholder="🥚"
                value={eggForm.emoji}
                onChange={e => setEggForm(f => ({ ...f, emoji: e.target.value }))}
                style={{ ...S.input, width: 50 }}
              />
            </Field>
            <Field label="Premise — what happens in the room (stage direction)" style={{ flex: 1, minWidth: 260 }}>
              <input
                placeholder="e.g. everyone breaks into a spontaneous crab rave"
                value={eggForm.premise}
                onChange={e => setEggForm(f => ({ ...f, premise: e.target.value }))}
                style={S.input}
              />
            </Field>
            <Field label="Card copy (optional)">
              <input
                placeholder="Shown on the reveal card"
                value={eggForm.card}
                onChange={e => setEggForm(f => ({ ...f, card: e.target.value }))}
                style={{ ...S.input, width: 210 }}
              />
            </Field>
            <Btn
              kind="primary"
              onClick={addEggWord}
              disabled={eggBusy || !eggForm.word || !eggForm.premise}
            >
              {eggBusy ? '...' : 'Mint'}
            </Btn>
          </div>
          {eggWords.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {eggWords.map(w => (
                    <tr key={w.word} style={{ borderBottom: '1px solid #222b38' }}>
                      <td style={{ ...S.td, fontWeight: 700 }}>{w.emoji} {w.word}</td>
                      <td style={{ ...S.td, color: '#9aa7b8', maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.premise}</td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <Btn
                          kind="danger"
                          onClick={() => deleteEggWord(w.word)}
                          disabled={eggBusy}
                          style={{ padding: '4px 10px', fontSize: 12 }}
                        >
                          Delete
                        </Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          id="kick"
          accent="#c46a2e"
          title="👢 Kick by name"
          blurb="Kicks every human player whose name matches (human players only). Current space = whichever game row is expanded in the Spaces table below."
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Field label="Player name" style={{ flex: 1, minWidth: 180 }}>
              <input
                type="text"
                value={kickName}
                onChange={e => setKickName(e.target.value)}
                placeholder="Exact name"
                style={S.input}
              />
            </Field>
            <Field label="Scope">
              <select
                value={kickScope}
                onChange={e => setKickScope(e.target.value)}
                style={S.input}
              >
                <option value="all_games">All spaces</option>
                <option value="current_game" disabled={!expandedGameId}>Current expanded space</option>
              </select>
            </Field>
            <Btn kind="danger" onClick={kickByName} disabled={!kickName.trim()} style={{ padding: '8px 18px' }}>
              Kick
            </Btn>
          </div>
        </Card>

        <Card
          id="agents"
          accent="#8a5cc6"
          title={`🤖 Marketplace agents (${adminAgents.length})`}
          blurb="Same lever as spaces, agent-flavored. Hide pulls it off /marketplace (is_public off). Retire also deactivates it (is_active off, stops roaming self-joins). Both reversible with Restore. No hard delete exists, by design."
        >
          {adminAgents.length === 0 ? (
            <p style={S.blurb}>No agents loaded (needs a deployed backend with adminListAgents).</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {adminAgents.map(a => {
                const live = a.is_public && a.is_active !== false;
                const busy = agentActionId === a.id;
                return (
                  <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 8px', borderRadius: 8, background: live ? 'transparent' : '#1b1420', opacity: busy ? 0.6 : 1 }}>
                    {a.avatar_url
                      ? <img src={a.avatar_url} alt="" style={{ width: 30, height: 30, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                      : <span style={{ width: 30, textAlign: 'center', flexShrink: 0 }}>🤖</span>}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#e8ecf3', fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.name}
                        <span style={{ color: '#8b98a9', fontWeight: 400, marginLeft: 8, fontSize: 12 }}>{a.source_platform || 'custom'}</span>
                      </div>
                      <div style={{ color: '#8b98a9', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.description || 'No description'}</div>
                      {Array.isArray(a.last_ips) && a.last_ips.length > 0 && (
                        <div
                          style={{ color: '#6f7d8f', fontSize: 11, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={a.last_ips.map(e => `${e.ip} — ${new Date(e.at).toLocaleString()}`).join('\n')}
                        >
                          🌐 {a.last_ips.map(e => e.ip).join(' · ')}
                        </div>
                      )}
                    </div>
                    {!a.is_public && <Pill tone="warn">hidden</Pill>}
                    {a.is_active === false && <Pill tone="warn">retired</Pill>}
                    {live ? (
                      <>
                        <Btn kind="blue" disabled={busy} onClick={() => agentAction(a, 'hide')} style={{ padding: '4px 10px', fontSize: 12 }}>Hide</Btn>
                        <Btn kind="danger" disabled={busy} onClick={() => agentAction(a, 'retire')} style={{ padding: '4px 10px', fontSize: 12 }}>Retire</Btn>
                      </>
                    ) : (
                      <Btn kind="blue" disabled={busy} onClick={() => agentAction(a, 'restore')} style={{ padding: '4px 10px', fontSize: 12 }}>Restore</Btn>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card
          id="music"
          accent="#c48ad6"
          title="🎵 Set a space's current song"
          blurb="Paste a space's share link (or its ID) and a YouTube link (or just words to search). The track becomes what's playing NOW and jumps to the top of that space's queue. This is the ONLY way to change a soundtrack after a space is built — music_url is write-once everywhere else. Space IDs are also in the Spaces table below."
        >
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Field label="Space link or ID" style={{ flex: 1, minWidth: 260 }}>
              <input
                type="text"
                value={musicGameId}
                onChange={e => setMusicGameId(e.target.value)}
                placeholder="https://dev.myspaice.ai/game-step?gameId=… or c8d10f1e-ebe0-4d0b-…"
                onKeyDown={e => { if (e.key === 'Enter' && !musicBusy) setSpaceMusic(); }}
                style={S.input}
              />
              {/* Echo what a pasted link resolved to. A silent parse would leave
                  the admin unable to tell "it read my link" from "it sent the
                  whole URL as an id and the server 404'd". Only shown when the
                  field is NOT already the bare id, so it never nags. */}
              {(() => {
                const parsed = spaceIdFromInput(musicGameId);
                if (!musicGameId.trim() || parsed === musicGameId.trim()) return null;
                const named = chatGames.find(g => String(g.id) === parsed);
                return (
                  <div style={{ fontSize: 11.5, marginTop: 4, color: isSpaceId(parsed) ? '#7fbf8f' : '#d0a24a' }}>
                    {isSpaceId(parsed) ? '→ ' : '⚠️ could not find an ID in that — sending: '}
                    <code>{parsed}</code>{named ? ` · ${named.name}` : ''}
                  </div>
                );
              })()}
            </Field>
            <Field label="YouTube URL or search words" style={{ flex: 2, minWidth: 280 }}>
              <input
                type="text"
                value={musicUrl}
                onChange={e => setMusicUrl(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !musicBusy) setSpaceMusic(); }}
                placeholder="https://www.youtube.com/watch?v=… or: rainy jazz cafe"
                style={S.input}
              />
            </Field>
            <Btn kind="blue" disabled={musicBusy} onClick={setSpaceMusic}>
              {musicBusy ? 'Setting…' : 'Set song'}
            </Btn>
          </div>
          <p style={{ ...S.blurb, marginTop: 10, marginBottom: 0 }}>
            Dead or unembeddable videos are rejected before anything is written. Listeners pick it
            up on their next poll. ⚠️ Anyone who set a personal radio URL in the ♫ panel overrides
            every space soundtrack on their own browser and won't hear this until they clear it.
          </p>
        </Card>

        <Card
          id="bugs"
          accent="#7a5a3a"
          title={`🐛 Bug reports${bugCounts ? ` (${bugCounts.new || 0} new)` : ''}`}
          blurb="What users sent from /bug and the What's New pill. Newest first. Also greppable in CloudWatch: [BugReport] 🐛 NEW."
        >
          <AdminBugReports adminFetch={adminFetch} adminKey={adminKey} onCounts={setBugCounts} />
        </Card>

        {/* 🎬📝 09-23 (MOVIE_NIGHT_UX_HANDOFF §3.7): what people answered on the
            credits card at the wrap of a house event. Empty until
            EVENT_FEEDBACK_ENABLED is live and a wrap has happened. */}
        <Card
          id="event-feedback"
          accent="#5a4a7a"
          title="🎬 Event feedback"
          blurb="The credits card: three taps and a line at the wrap of a house event, once per device. Tallies first, then every answer. CloudWatch: [EventFeedback] 🎬 NEW."
        >
          <AdminEventFeedback adminFetch={adminFetch} adminKey={adminKey} />
        </Card>

        {(() => { const baseGames = showProbeGames ? chatGames : chatGames.filter(g => !isProbeGame(g)); const probeCount = chatGames.length - chatGames.filter(g => !isProbeGame(g)).length; const isCompGame = g => g.competitive_mode || g.imposter_mode; const compCount = baseGames.filter(isCompGame).length; const compFiltered = hideCompGames ? baseGames.filter(g => !isCompGame(g)) : baseGames; const privateCount = compFiltered.filter(g => !g.is_shared).length; const visFiltered = showPrivateOnly ? compFiltered.filter(g => !g.is_shared) : compFiltered; const q = gameSearch.trim().toLowerCase(); const searched = q ? visFiltered.filter(g => `${g.name || ''} ${g.id}`.toLowerCase().includes(q)) : visFiltered; const visibleGames = [...searched].sort((a, b) => gamesSort === 'used' ? usedRank(b) - usedRank(a) : new Date(b.created_at) - new Date(a.created_at)); return (
        <Card
          id="games"
          accent="#4a6079"
          title={`🎮 Spaces / chat games (${visibleGames.length})`}
          blurb={`${!showAllGames && visibleGames.length > GAMES_SHOWN ? `Top ${GAMES_SHOWN} of ${visibleGames.length} chat-game instances` : 'Every chat-game instance'}, ${gamesSort === 'used' ? 'most recently used first (a human touch outranks resident AI chatter)' : 'newest first by creation date'}. Expand a row to see its human players (and kick from there).`}
        >
          {orSpend && (
            <div style={{ marginBottom: 10, fontSize: 12.5, color: '#9aa7b8' }}>
              💸 OpenRouter spend (whole account; per-space cost isn't recorded):{' '}
              <strong style={{ color: '#e0c36a' }}>${Number(orSpend.usage_daily ?? 0).toFixed(2)} today</strong>
              {' · '}${Number(orSpend.usage_weekly ?? 0).toFixed(2)} this week
              {' · '}${Number(orSpend.usage_monthly ?? 0).toFixed(2)} this month
              {orSpend.credits_remaining != null && ` · $${Number(orSpend.credits_remaining).toFixed(2)} credits left`}
            </div>
          )}
          {spendAttr && (
            <div style={{ marginBottom: 10, fontSize: 12.5, color: '#9aa7b8' }}>
              📊 Per-space attribution (last {spendAttr.days}d, estimated):{' '}
              <strong style={{ color: '#e0c36a' }}>
                ${((spendAttr.by_space || []).reduce((n, r) => n + (r.cents || 0), 0) / 100).toFixed(2)} attributed
              </strong>
              {' · '}
              <button
                onClick={() => setShowSpendAttr(v => !v)}
                style={{ background: 'none', border: 'none', color: '#7fa7d4', cursor: 'pointer', fontSize: 12.5, padding: 0, textDecoration: 'underline' }}
              >
                {showSpendAttr ? 'hide breakdown' : 'show breakdown'}
              </button>
              {showSpendAttr && (
                <div style={{ marginTop: 6, display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead><tr style={{ color: '#6f7d8f', textAlign: 'left' }}>
                      <th style={{ paddingRight: 10 }}>Space</th><th style={{ paddingRight: 10 }}>calls</th><th style={{ paddingRight: 10 }}>7d</th><th>today</th>
                    </tr></thead>
                    <tbody>
                      {(spendAttr.by_space || []).slice(0, 15).map((r) => (
                        <tr key={String(r.space_id)}>
                          <td style={{ paddingRight: 10 }}>{r.name || (r.space_id ? `#${r.space_id}` : '(unattributed)')}</td>
                          <td style={{ paddingRight: 10 }}>{r.calls}</td>
                          <td style={{ paddingRight: 10 }}>${((r.cents || 0) / 100).toFixed(3)}</td>
                          <td>${((r.cents_today || 0) / 100).toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead><tr style={{ color: '#6f7d8f', textAlign: 'left' }}>
                      <th style={{ paddingRight: 10 }}>Trigger</th><th style={{ paddingRight: 10 }}>calls</th><th>7d</th>
                    </tr></thead>
                    <tbody>
                      {(spendAttr.by_trigger || []).map((r) => (
                        <tr key={r.trigger_class}>
                          <td style={{ paddingRight: 10 }}>{r.trigger_class}</td>
                          <td style={{ paddingRight: 10 }}>{r.calls}</td>
                          <td>${((r.cents || 0) / 100).toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead><tr style={{ color: '#6f7d8f', textAlign: 'left' }}>
                      <th style={{ paddingRight: 10 }}>Layer · model</th><th style={{ paddingRight: 10 }}>calls</th><th>7d</th>
                    </tr></thead>
                    <tbody>
                      {(spendAttr.by_layer || []).map((r, i) => (
                        <tr key={`${r.layer}-${r.model}-${i}`}>
                          <td style={{ paddingRight: 10 }}>{r.layer} · {r.model}</td>
                          <td style={{ paddingRight: 10 }}>{r.calls}</td>
                          <td>${((r.cents || 0) / 100).toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <div style={{ marginBottom: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn onClick={fetchChatGames} disabled={loading} style={{ padding: '4px 12px', fontSize: 12 }}>
              ↻ Refresh list
            </Btn>
            <a
              href="/admin/activity"
              style={{ padding: '4px 12px', fontSize: 12, borderRadius: 6, background: '#1c2e46', color: '#9ec5ff', border: '1px solid #35597f', textDecoration: 'none', alignSelf: 'center' }}
              title="Every person and agent that joined or acted, across all spaces, newest first."
            >
              📡 Recent activity ↗
            </a>
            <input
              value={gameSearch}
              onChange={(e) => setGameSearch(e.target.value)}
              placeholder="Search name or id…"
              style={{
                padding: '4px 10px', fontSize: 12, minWidth: 190,
                background: '#151d29', color: '#e8ecf3',
                border: '1px solid #39455a', borderRadius: 6, outline: 'none',
              }}
            />
            {gameSearch && (
              <Btn onClick={() => setGameSearch('')} kind="subtle" style={{ padding: '4px 12px', fontSize: 12 }}>
                Clear “{gameSearch.slice(0, 16)}”
              </Btn>
            )}
            <Btn
              onClick={() => setGamesSort(s => (s === 'used' ? 'created' : 'used'))}
              kind={gamesSort === 'used' ? 'warn' : 'subtle'}
              style={{ padding: '4px 12px', fontSize: 12 }}
              title="Recently used ranks by the newest human touch, then any activity, then creation date."
            >
              {gamesSort === 'used' ? '🔥 Recently used' : '🕓 Newest created'}
            </Btn>
            {visibleGames.length > GAMES_SHOWN && (
              <Btn onClick={() => setShowAllGames(v => !v)} style={{ padding: '4px 12px', fontSize: 12 }}>
                {showAllGames ? `Show newest ${GAMES_SHOWN} only` : `Show all ${visibleGames.length}`}
              </Btn>
            )}
            {probeCount > 0 && (
              <Btn onClick={() => setShowProbeGames(v => !v)} style={{ padding: '4px 12px', fontSize: 12 }}>
                {showProbeGames ? `Hide ${probeCount} probe game${probeCount > 1 ? 's' : ''}` : `Show ${probeCount} probe game${probeCount > 1 ? 's' : ''}`}
              </Btn>
            )}
            {compCount > 0 && (
              <Btn onClick={() => setHideCompGames(v => !v)} kind={hideCompGames ? 'warn' : 'subtle'} style={{ padding: '4px 12px', fontSize: 12 }}>
                {hideCompGames ? `Show ${compCount} competitive` : `🏆 Hide ${compCount} competitive`}
              </Btn>
            )}
            {privateCount > 0 && (
              <Btn onClick={() => setShowPrivateOnly(v => !v)} kind={showPrivateOnly ? 'warn' : 'subtle'} style={{ padding: '4px 12px', fontSize: 12 }}>
                {showPrivateOnly ? 'Show all visibilities' : `🔒 Only the ${privateCount} private`}
              </Btn>
            )}
          </div>
          {loading ? (
            <p style={{ color: '#8b98a9' }}>Loading...</p>
          ) : visibleGames.length === 0 ? (
            <p style={{ color: '#8b98a9' }}>No chat-game instances found.</p>
          ) : (
            <>
              <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #39455a' }}>
                      <th style={S.th}>Name</th>
                      <th style={S.th} title="Newest human touch (seat joined or acted). The 👤-less line is any activity, resident AI included.">Last used</th>
                      <th style={S.th}>Created</th>
                      <th style={S.th}>Status</th>
                      <th style={S.th}>Visibility</th>
                      <th style={S.th}>Creator IP</th>
                      <th style={S.th}>Link</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Rounds</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Players</th>
                      <th style={{ ...S.th, textAlign: 'right' }} title="Average join→last-action span of humans who acted. No presence tracking exists, so this is engagement span, not tab-open time.">Avg human time</th>
                      <th style={S.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllGames ? visibleGames : visibleGames.slice(0, GAMES_SHOWN)).map(g => {
                      const st = gameStatus(g);
                      return (
                        <React.Fragment key={g.id}>
                          <tr style={{ borderBottom: '1px solid #222b38' }}>
                            <td style={S.td}>{g.name || g.id.substring(0, 8)}</td>
                            <td style={{ ...S.td, whiteSpace: 'nowrap' }} title={g.last_human_at ? `Human: ${new Date(g.last_human_at).toLocaleString()}${g.last_activity_at ? `\nAny activity: ${new Date(g.last_activity_at).toLocaleString()}` : ''}` : 'No human has ever touched this space'}>
                              {fmtAgo(g.last_human_at)
                                ? <span style={{ color: '#e8ecf3' }}>👤 {fmtAgo(g.last_human_at)} ago</span>
                                : <span style={{ color: '#4a5568' }}>—</span>}
                              {g.last_activity_at && (
                                <span style={{ display: 'block', color: '#8b98a9', fontSize: 11 }}>
                                  any: {fmtAgo(g.last_activity_at)} ago
                                </span>
                              )}
                            </td>
                            <td style={S.td}>
                              {new Date(g.created_at).toLocaleDateString()}
                              <span style={{ display: 'block', color: '#8b98a9', fontSize: 11 }}>
                                {new Date(g.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                              </span>
                            </td>
                            <td style={S.td}><Pill tone={st.tone}>{st.text}</Pill></td>
                            <td style={S.td}>{g.space_closed ? <Pill tone="live">🚫 Removed</Pill> : g.is_shared ? <Pill tone="idle">Public</Pill> : <Pill tone="warn">🔒 Private</Pill>}</td>
                            <td style={{ ...S.td, whiteSpace: 'nowrap' }} title="First real person's join IP (captured from 08-11 on). 🤖 = an agent seat predates every person; its IP is the inviter/operator trail, never the creator">
                              {g.creator_is_agent && (
                                <span style={{ display: 'block', color: '#d9a441', fontSize: 11 }}>
                                  🤖 {g.creator_agent_name || 'agent'} first
                                  {g.creator_agent_ip && (
                                    <> · <span style={{ fontFamily: FONT_MONO }}>{g.creator_agent_ip}</span></>
                                  )}
                                </span>
                              )}
                              {g.creator_ip ? (
                                <>
                                  <span style={{ fontFamily: FONT_MONO, fontSize: 12 }}>{g.creator_ip}</span>
                                  {geoByIp[g.creator_ip] && (
                                    <span style={{ display: 'block', color: '#8b98a9', fontSize: 11 }}>
                                      {[geoByIp[g.creator_ip].city, geoByIp[g.creator_ip].region, geoByIp[g.creator_ip].country].filter(Boolean).join(', ')}
                                    </span>
                                  )}
                                </>
                              ) : (!g.creator_is_agent && <span style={{ color: '#4a5568' }}>—</span>)}
                            </td>
                            <td style={S.td}>
                              <a
                                href={`/game-step?gameId=${g.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#6ea8ff', fontSize: 12.5, textDecoration: 'none', whiteSpace: 'nowrap' }}
                              >
                                Open ↗
                              </a>
                            </td>
                            <td style={{ ...S.td, textAlign: 'right' }}>{g.round_count}</td>
                            <td style={{ ...S.td, textAlign: 'right' }}>{g.player_count}</td>
                            <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }} title={g.active_humans ? `${g.active_humans} human${g.active_humans > 1 ? 's' : ''} acted` : 'No human ever acted here'}>
                              {fmtAvgMinutes(g.avg_human_minutes) || <span style={{ color: '#4a5568' }}>—</span>}
                            </td>
                            <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                              <Btn onClick={() => toggleExpandGame(g.id)} style={{ padding: '4px 12px', fontSize: 12 }}>
                                {expandedGameId === g.id ? 'Hide' : 'Humans'}
                              </Btn>
                              <Btn kind="danger" onClick={() => setRemoveTarget(g)} style={{ padding: '4px 12px', fontSize: 12, marginLeft: 6 }}>
                                {g.space_closed ? 'Removed…' : 'Remove…'}
                              </Btn>
                            </td>
                          </tr>
                          {expandedGameId === g.id && (
                            <tr>
                              <td colSpan={11} style={{ padding: 12, background: '#0c1016' }}>
                                {!humansByGame[g.id] ? (
                                  <span style={{ color: '#8b98a9' }}>Loading...</span>
                                ) : humansByGame[g.id].length === 0 ? (
                                  <span style={{ color: '#8b98a9' }}>Nobody has ever sat here (no people, no agents).</span>
                                ) : (
                                  <table style={{ width: '100%', fontSize: 12.5 }}>
                                    <tbody>
                                      {humansByGame[g.id].map(p => (
                                        <tr key={p.id}>
                                          <td style={{ padding: 4, color: p.kicked_at ? '#8b98a9' : '#e8edf4' }}>
                                            <SeatKind p={p} />
                                            {p.name}
                                            {p.kicked_at && <span style={{ marginLeft: 8, color: '#c88', fontSize: 11 }}>kicked {new Date(p.kicked_at).toLocaleString()}</span>}
                                            {!p.kicked_at && p.is_spectator && <span style={{ marginLeft: 8, color: '#8b98a9', fontSize: 11 }}>spectator</span>}
                                            {p.created_via && (
                                              <span style={{ display: 'block', color: '#6f7d8f', fontSize: 11 }}>
                                                via {p.created_via}
                                                {p.agent_id && <> · agent <a href={`/agents/${p.agent_id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#6ea8ff', textDecoration: 'none' }}>{p.agent_id.substring(0, 8)}</a></>}
                                              </span>
                                            )}
                                          </td>
                                          <td style={{ padding: 4, color: '#7a8698', fontFamily: FONT_MONO }}>{p.id.substring(0, 8)}</td>
                                          <td style={{ padding: 4, color: '#7a8698', fontFamily: FONT_MONO }} title={ipTitle(p)}>
                                            {p.join_ip || '—'}
                                            {p.join_ip && geoByIp[p.join_ip] && (
                                              <span style={{ display: 'block', color: '#8b98a9', fontSize: 11, fontFamily: 'inherit' }}>
                                                {[geoByIp[p.join_ip].city, geoByIp[p.join_ip].region, geoByIp[p.join_ip].country].filter(Boolean).join(', ')}
                                              </span>
                                            )}
                                            {Array.isArray(p.join_ips) && p.join_ips.length > 1 && (
                                              <span style={{ display: 'block', color: '#6f7d8f', fontSize: 11 }}>
                                                +{p.join_ips.length - 1} earlier
                                              </span>
                                            )}
                                          </td>
                                          <td style={{ padding: 4, color: '#7a8698', whiteSpace: 'nowrap' }} title={p.created_at ? `Joined ${new Date(p.created_at).toLocaleString()}` : 'No join timestamp on this seat'}>
                                            {p.created_at ? (
                                              <>
                                                {new Date(p.created_at).toLocaleDateString([], { month: 'numeric', day: 'numeric' })} {new Date(p.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                                {fmtAgo(p.created_at) && (
                                                  <span style={{ display: 'block', color: '#8b98a9', fontSize: 11 }}>{fmtAgo(p.created_at)} ago</span>
                                                )}
                                              </>
                                            ) : '—'}
                                          </td>
                                          <td style={{ padding: 4, textAlign: 'right' }}>
                                            {!p.kicked_at && (
                                              <Btn
                                                kind="danger"
                                                onClick={() => kickPlayer(g.id, p.id)}
                                                disabled={!!kicking[p.id]}
                                                style={{ padding: '4px 10px', fontSize: 12, cursor: kicking[p.id] ? 'wait' : 'pointer' }}
                                              >
                                                {kicking[p.id] ? 'Kicking...' : 'Kick'}
                                              </Btn>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ paddingTop: 14, borderTop: '1px dashed #7c2a2e' }}>
                <span style={{ ...S.groupLabel, color: '#ff7b7b', display: 'block', marginBottom: 8 }}>Danger zone</span>
                <Btn kind="danger" onClick={handleDeleteAll} disabled={deleting} style={{ padding: '10px 20px', fontSize: 14 }}>
                  {deleting ? 'Backing up & deleting...' : `Backup & Delete All ${chatGames.length} Chat Games`}
                </Btn>
              </div>
            </>
          )}
        </Card>
        ); })()}

        {removeTarget && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => { if (!removing) setRemoveTarget(null); }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(460px, 92vw)', background: '#151b26', border: '1px solid #39455a', borderRadius: 10, padding: '20px 22px' }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#fff' }}>
                Remove "{removeTarget.name || removeTarget.id.substring(0, 8)}"?
              </h3>
              <p style={S.blurb}>
                {removeTarget.space_closed
                  ? 'This space is already temporarily removed (closed + hidden). Restore it, delete it for good, or leave it be.'
                  : 'Both remove options kick every human out first.'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Btn
                  kind="danger"
                  disabled={removing}
                  onClick={() => {
                    if (window.confirm(`Permanently delete "${removeTarget.name || removeTarget.id}" and ALL its data?\n\nThe row is backed up to game_instances_backup_2_17_26a first, but this is otherwise final.`)) {
                      removeSpace('permanent');
                    }
                  }}
                >
                  {removing ? 'Working…' : '🔥 Permanently — kick everyone + delete the data'}
                </Btn>
                {removeTarget.space_closed ? (
                  <Btn kind="primary" disabled={removing} onClick={() => removeSpace('restore')}>
                    {removing ? 'Working…' : '♻️ Restore — reopen the space (stays off /explore)'}
                  </Btn>
                ) : (
                  <Btn kind="warn" disabled={removing} onClick={() => removeSpace('temporary')}>
                    {removing ? 'Working…' : '⏸ Temporarily — kick everyone, hide from /explore, link stops working'}
                  </Btn>
                )}
                <Btn disabled={removing} onClick={() => setRemoveTarget(null)}>Nevermind</Btn>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
